import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Users, 
  Building2, 
  Calendar, 
  Clock,
  MapPin,
  GripVertical,
  X,
  List,
  Grid3x3,
  Map,
  Search,
  Check,
  CheckCircle,
  SkipForward,
  ArrowRight,
  MoreVertical,
  RotateCcw,
  Trash2,
  FileText,
  Eye,
  ChevronUp,
  ChevronDown,
  Droplet,
  History,
  Loader2,
  AlertTriangle,
  HelpCircle,
  Package,
  Plus,
  Copy,
  Edit
} from 'lucide-react';
import { clientsAPI, serviceCallsAPI, usersAPI, cspireAPI, franklinAPI } from '@shared/apiClient';
import { FlagIcon } from '@shared/flagIcons';
import { formatDate, formatDateTime } from '@shared/utils';
import toast from 'react-hot-toast';
import AssignClientModal from '../components/AssignClientModal';
import ClientsMap from '../components/ClientsMap';
import ClientDetailsModal from '../components/ClientDetailsModal';
import ClientFormModal from '../components/ClientFormModal';
import MonthlyOilFiltersModal from '../components/MonthlyOilFiltersModal';
import MonthlyFuelFiltersModal from '../components/MonthlyFuelFiltersModal';
import MonthPairComparisonModal from '../components/MonthPairComparisonModal';
import CspireSiteDetailsModal from '../components/CspireSiteDetailsModal';
import FranklinSiteDetailsModal from '../components/FranklinSiteDetailsModal';
import ReturnToTechModal from '../components/ReturnToTechModal';
import { ServiceCallEditModal, ServiceCallDetailsModal } from './ServiceCalls';
import { formatCspireAssignmentDisplayName } from '../utils/cspireDisplay';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const StatCard = ({ title, value, icon: Icon, color }) => {
  const displayValue = typeof value === 'number' ? value : (parseInt(value) || 0);
  
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 whitespace-nowrap overflow-hidden">{title}</dt>
              <dd className="text-2xl font-semibold text-gray-900">{displayValue}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientFlagIcons = ({ flags }) => {
  if (!flags || flags.length === 0) return null;

  const visibleFlags = flags.slice(0, 5);
  const remaining = flags.length - visibleFlags.length;
  const remainingTitles = flags.slice(5).map((flag) => flag.name).join(', ');

  return (
    <div className="flex items-center gap-1">
      {visibleFlags.map((flag) => (
        <span key={flag.id} title={flag.name}>
          <FlagIcon
            iconName={flag.icon}
            className={`h-3 w-3 ${flag.isActive ? 'text-gray-600' : 'text-gray-400'}`}
            title={flag.name}
          />
        </span>
      ))}
      {remaining > 0 && (
        <span
          className="text-xs text-gray-500"
          title={remainingTitles}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
};

const CspireAssignModal = ({ workOrder, isOpen, onClose, technicians, schedules, onAssign }) => {
  const [formData, setFormData] = useState({
    technicianId: '',
    scheduleId: '',
    scheduledDate: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        technicianId: '',
        scheduleId: workOrder?.scheduleId || '',
        scheduledDate: '',
        notes: ''
      });
    }
  }, [isOpen, workOrder]);

  if (!isOpen || !workOrder) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.technicianId) {
      toast.error('Please select a technician');
      return;
    }
    onAssign(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Assign CSpire Work Order</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-gray-50 p-3 rounded-md mb-4 text-sm">
          <div className="font-medium text-gray-900">WO# {workOrder.workOrderNumber}</div>
          <div className="text-gray-600">{workOrder.locationName || workOrder.site?.locationName || 'CSpire Site'}</div>
          {workOrder.site?.siteId && (
            <div className="text-gray-500">Site ID: {workOrder.site.siteId}</div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Technician *
            </label>
            <select
              value={formData.technicianId}
              onChange={(e) => setFormData({ ...formData, technicianId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a technician</option>
              {technicians.map((tech) => (
                <option key={tech.id} value={tech.id}>
                  {tech.firstName} {tech.lastName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price Schedule
            </label>
            <select
              value={formData.scheduleId}
              onChange={(e) => setFormData({ ...formData, scheduleId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select schedule...</option>
              {schedules.map((schedule) => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.name} ({new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(schedule.amount || 0)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scheduled Date (optional)
            </label>
            <input
              type="date"
              value={formData.scheduledDate}
              onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes for Technician
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional notes..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Assign Work Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CspireWorkOrderModal = ({ workOrder, isOpen, onClose, schedules, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    scheduleId: '',
    invoiceInstructions: ''
  });

  useEffect(() => {
    if (isOpen && workOrder) {
      setFormData({
        scheduleId: workOrder.scheduleId || '',
        invoiceInstructions: workOrder.invoiceInstructions || ''
      });
    }
  }, [isOpen, workOrder]);

  if (!isOpen || !workOrder) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">CSpire Work Order</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="font-medium text-gray-900">WO# {workOrder.workOrderNumber}</div>
            <div className="text-gray-600">{workOrder.locationName || workOrder.site?.locationName || 'CSpire Site'}</div>
            {workOrder.site?.siteId && <div className="text-gray-500">Site ID: {workOrder.site.siteId}</div>}
            {workOrder.site?.addressLine1 && (
              <div className="text-gray-500">{workOrder.site.addressLine1}</div>
            )}
            {(workOrder.site?.city || workOrder.site?.state || workOrder.site?.zipCode) && (
              <div className="text-gray-500">
                {[workOrder.site?.city, workOrder.site?.state, workOrder.site?.zipCode].filter(Boolean).join(', ')}
              </div>
            )}
            {(workOrder.site?.latitude || workOrder.site?.longitude) && (
              <div className="text-gray-500">
                {workOrder.site?.latitude || 'N/A'}, {workOrder.site?.longitude || 'N/A'}
              </div>
            )}
          </div>
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="text-gray-600">Due Date: {workOrder.dueDate ? formatDate(workOrder.dueDate) : 'N/A'}</div>
            <div className="text-gray-600">Status: {workOrder.status || 'N/A'}</div>
            {workOrder.severity && <div className="text-gray-600">Severity: {workOrder.severity}</div>}
            {workOrder.subject && <div className="text-gray-600">Subject: {workOrder.subject}</div>}
            {workOrder.reason && <div className="text-gray-600">Reason: {workOrder.reason}</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="font-medium text-gray-900">Opened By</div>
            {workOrder.openedByName && <div className="text-gray-600">{workOrder.openedByName}</div>}
            {workOrder.openedByEmail && <div className="text-gray-600">{workOrder.openedByEmail}</div>}
            {workOrder.openedByPhone && <div className="text-gray-600">{workOrder.openedByPhone}</div>}
            {!workOrder.openedByName && !workOrder.openedByEmail && !workOrder.openedByPhone && (
              <div className="text-gray-500">N/A</div>
            )}
          </div>
          <div className="bg-gray-50 p-3 rounded-md">
            <div className="font-medium text-gray-900">Description</div>
            {workOrder.description
              ? <div className="text-gray-600 whitespace-pre-wrap">{workOrder.description}</div>
              : <div className="text-gray-500">N/A</div>}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price Schedule
            </label>
            <select
              value={formData.scheduleId}
              onChange={(e) => setFormData({ ...formData, scheduleId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select schedule...</option>
              {schedules.map((schedule) => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.name} ({new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(schedule.amount || 0)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Instructions
            </label>
            <textarea
              value={formData.invoiceInstructions}
              onChange={(e) => setFormData({ ...formData, invoiceInstructions: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex flex-wrap justify-between gap-3 pt-4 border-t">
            <div>
              <button
                type="button"
                onClick={() => onDelete?.(workOrder)}
                className="btn btn-danger flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>{workOrder.serviceCallId ? 'Force Delete' : 'Delete Work Order'}</span>
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Close
              </button>
              <button type="submit" className="btn btn-primary">
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const CspireCreateWorkOrderModal = ({ isOpen, onClose, sites, onCreate }) => {
  const [formData, setFormData] = useState({
    workOrderNumber: '',
    siteId: '',
    subject: '',
    description: '',
    severity: '',
    status: '',
    dueDate: '',
    reason: '',
    openedByName: '',
    openedByEmail: '',
    openedByPhone: '',
    invoiceInstructions: ''
  });
  const [siteSearch, setSiteSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        workOrderNumber: '', siteId: '', subject: '', description: '',
        severity: '', status: '', dueDate: '', reason: '',
        openedByName: '', openedByEmail: '', openedByPhone: '', invoiceInstructions: ''
      });
      setSiteSearch('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredSites = sites.filter((s) => {
    if (!siteSearch.trim()) return true;
    const q = siteSearch.toLowerCase();
    return (s.siteId || '').toLowerCase().includes(q)
      || (s.locationName || '').toLowerCase().includes(q)
      || (s.city || '').toLowerCase().includes(q);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.workOrderNumber.trim()) {
      toast.error('Work order number is required');
      return;
    }
    if (!formData.siteId) {
      toast.error('Please select a site');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {};
      Object.entries(formData).forEach(([k, v]) => {
        payload[k] = typeof v === 'string' ? (v.trim() || null) : v;
      });
      if (payload.dueDate) {
        payload.dueDate = new Date(payload.dueDate).toISOString();
      }
      await onCreate(payload);
      onClose();
    } catch (error) {
      // Error toast handled by mutation onError
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedSite = sites.find((s) => s.id === formData.siteId);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create CSpire Work Order</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Order Number *</label>
              <input
                type="text"
                required
                value={formData.workOrderNumber}
                onChange={(e) => setFormData({ ...formData, workOrderNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. WO-12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site *</label>
            <input
              type="text"
              placeholder="Search sites by ID, name, or city..."
              value={siteSearch}
              onChange={(e) => setSiteSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 mb-2"
            />
            {selectedSite && (
              <div className="mb-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm flex items-center justify-between">
                <span>{selectedSite.locationName || selectedSite.siteId} ({selectedSite.siteId})</span>
                <button type="button" onClick={() => setFormData({ ...formData, siteId: '' })} className="text-indigo-600 hover:text-indigo-800">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {!selectedSite && (
              <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md">
                {filteredSites.length === 0 ? (
                  <div className="p-2 text-sm text-gray-500">No sites found.</div>
                ) : (
                  filteredSites.slice(0, 20).map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, siteId: s.id })}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-0"
                    >
                      <span className="font-medium">{s.locationName || s.siteId}</span>
                      <span className="text-gray-500 ml-2">({s.siteId})</span>
                      {s.city && <span className="text-gray-400 ml-1">- {s.city}, {s.state}</span>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <input
                type="text"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select...</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select...</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Opened By (optional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Name"
                value={formData.openedByName}
                onChange={(e) => setFormData({ ...formData, openedByName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.openedByEmail}
                onChange={(e) => setFormData({ ...formData, openedByEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <input
                type="text"
                placeholder="Phone"
                value={formData.openedByPhone}
                onChange={(e) => setFormData({ ...formData, openedByPhone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Instructions</label>
            <textarea
              value={formData.invoiceInstructions}
              onChange={(e) => setFormData({ ...formData, invoiceInstructions: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.workOrderNumber.trim() || !formData.siteId}
              className="btn btn-primary flex items-center gap-1.5"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create Work Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CspireDuplicatesModal = ({ isOpen, onClose, onDelete }) => {
  const [duplicates, setDuplicates] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
      cspireAPI.findDuplicateWorkOrders()
        .then((res) => setDuplicates(res.data.duplicates || {}))
        .catch(() => setError('Failed to fetch duplicates'))
        .finally(() => setIsLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const dupeKeys = Object.keys(duplicates);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Duplicate Work Orders</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600 mr-2" />
            <span className="text-sm text-gray-600">Searching for duplicates...</span>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
        )}

        {!isLoading && !error && dupeKeys.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <p className="text-sm">No duplicate work orders found.</p>
          </div>
        )}

        {!isLoading && dupeKeys.length > 0 && (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {dupeKeys.map((woNumber) => (
              <div key={woNumber} className="border border-orange-200 rounded-lg p-3 bg-orange-50">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  <span className="font-medium text-gray-900">WO# {woNumber}</span>
                  <span className="text-xs bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded">
                    {duplicates[woNumber].length} copies
                  </span>
                </div>
                <div className="space-y-2">
                  {duplicates[woNumber].map((wo) => (
                    <div key={wo.id} className="flex items-center justify-between bg-white rounded p-2 border border-gray-200 text-sm">
                      <div>
                        <div className="text-gray-700">
                          {wo.site?.locationName || wo.locationName || 'Unknown Site'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Created: {formatDate(wo.createdAt)}
                          {wo.serviceCall && (
                            <span className="ml-2 text-blue-600">
                              Assigned to {wo.serviceCall.technician?.firstName} {wo.serviceCall.technician?.lastName}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onDelete(wo)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Delete this duplicate"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button type="button" onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
};

const ServiceCallCard = ({ serviceCall, onClick, onReassign, isDragging, onDragStart, onDragEnd, showCompletedDate = false, displayName, statusBadge }) => {
  const client = serviceCall.client;

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', serviceCall.id);
    onDragStart?.(serviceCall);
  };

  const visitCount = serviceCall.visits?.length || 0;
  let clientDisplayName = displayName || '';
  if (!clientDisplayName) {
    if (serviceCall.isFranklin) {
      const fs = serviceCall.franklinSite;
      clientDisplayName = `Franklin - ${fs?.locationName || fs?.siteId || 'Site'}`;
    } else if (serviceCall.isCspire) {
      clientDisplayName = formatCspireAssignmentDisplayName(serviceCall.cspireWorkOrder);
    } else if (client) {
      clientDisplayName = `${client.contactFirstName || ''} ${client.contactLastName || ''}`.trim();
    } else {
      clientDisplayName = 'Unknown';
    }
  }

  return (
    <div
      draggable={!!onDragStart}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`bg-white border border-gray-200 rounded-lg p-3 mb-2 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start space-x-2">
        {onDragStart && <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 truncate">
              {clientDisplayName}
            </p>
            {visitCount > 0 && (
              <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded flex items-center gap-1">
                <History className="h-3 w-3" />
                {visitCount}
              </span>
            )}
            {statusBadge && (
              <span className={`flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            )}
          </div>
          {showCompletedDate && serviceCall.completedDate && (
            <p className="text-xs text-gray-500 mt-1">
              Completed: {formatDate(serviceCall.completedDate)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const TechnicianColumn = ({ technician, assignments, onDrop, onDragOver, isDraggingOver, onAssignmentDragStart, onAssignmentDragEnd, draggedAssignment, onEditNotes }) => {
  const assignmentCount = assignments.length;

  return (
    <div
      className={`h-full flex flex-col bg-white rounded-lg border-2 shadow-sm ${
        isDraggingOver ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      } transition-colors`}
      onDrop={onDrop}
      onDragOver={onDragOver}
    >
      {/* Column Header */}
      <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200 px-3 py-2 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {technician.firstName} {technician.lastName}
            </h3>
            <p className="text-xs text-gray-500 truncate">{technician.email}</p>
          </div>
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex-shrink-0 ml-2">
            {assignmentCount}
          </span>
        </div>
      </div>

      {/* Assignments List */}
      <div className="flex-1 p-2 overflow-y-auto min-h-0">
        {assignments.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400">
            <p>Drop clients here</p>
          </div>
        ) : (
          assignments.map((assignment) => {
            const client = assignment.client;
            const cspireWorkOrder = assignment.cspireWorkOrder;
            const franklinSite = assignment.franklinSite;
            const isCspire = !!cspireWorkOrder || assignment.isCspire;
            const isFranklin = !!franklinSite || assignment.isFranklin;
            if (!client && !cspireWorkOrder && !franklinSite) return null;
            
            const isDragging = draggedAssignment?.id === assignment.id;
            const hasNotes = assignment.customerNotes && assignment.customerNotes.trim().length > 0;
            const displayName = isFranklin
              ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}`
              : isCspire
              ? formatCspireAssignmentDisplayName(cspireWorkOrder)
              : (client.isCommercial && client.companyName 
                ? client.companyName 
                : `${client.contactFirstName} ${client.contactLastName}`);
            const location = isFranklin
              ? ([franklinSite?.city, franklinSite?.state].filter(Boolean).join(', ') || 'Franklin Site')
              : isCspire
              ? (cspireWorkOrder.site?.city && cspireWorkOrder.site?.state
                ? `${cspireWorkOrder.site.city}, ${cspireWorkOrder.site.state}`
                : (cspireWorkOrder.locationName || cspireWorkOrder.site?.locationName || 'CSpire Site'))
              : `${client.city}, ${client.state}`;
            const isDraggable = !!client || isCspire || isFranklin;
            
            return (
              <div
                key={assignment.id}
                draggable={isDraggable}
                onDragStart={(e) => {
                  if (!isDraggable) return;
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', assignment.id);
                  onAssignmentDragStart?.(assignment);
                }}
                onDragEnd={onAssignmentDragEnd}
                className={`bg-white border border-gray-200 rounded-lg p-3 mb-2 cursor-move hover:border-blue-400 hover:shadow-md transition-all ${
                  isDragging ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start space-x-2">
                  <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {location}
                    </p>
                    {isCspire && (
                      <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        CSpire
                      </span>
                    )}
                    {isFranklin && (
                      <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                        Franklin
                      </span>
                    )}
                    {hasNotes && (
                      <p className="text-xs text-gray-600 mt-1 italic truncate" title={assignment.customerNotes}>
                        📝 {assignment.customerNotes}
                      </p>
                    )}
                  </div>
                  {onEditNotes && client && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditNotes(assignment);
                      }}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 rounded"
                      title="Edit notes"
                    >
                      <FileText className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const AddNotesModal = ({ client, technician, isOpen, onClose, onConfirm, existingNotes = '', isEdit = false, heading = null }) => {
  const [notes, setNotes] = useState(existingNotes);

  useEffect(() => {
    if (isOpen) {
      setNotes(existingNotes);
    }
  }, [isOpen, existingNotes]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(notes);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-gray-50 px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {heading || (isEdit ? 'Edit Notes' : 'Add Notes to Assignment')}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">
                    {client?.isCommercial && client?.companyName
                      ? client.companyName
                      : `${client?.contactFirstName || ''} ${client?.contactLastName || ''}`.trim() || client?.companyName || 'Customer'}
                  </span>
                  <span className="mx-2">•</span>
                  {client?.city}, {client?.state}
                </p>
                {technician && (
                  <p className="text-sm text-gray-500 mt-1">
                    {isEdit ? 'Assigned to' : 'Assigning to'}: {technician.firstName} {technician.lastName}
                  </p>
                )}
              </div>
            </div>

            <div className="px-4 py-5 sm:p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any special instructions or notes for the technician..."
                  autoFocus
                />
                <p className="mt-1 text-xs text-gray-500">
                  These notes will be visible to the technician when they view this assignment.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {isEdit ? 'Update Notes' : 'Confirm Assignment'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};


export default function Dashboard() {
  const [draggedClient, setDraggedClient] = useState(null);
  const [draggedAssignment, setDraggedAssignment] = useState(null);
  const [draggedReturnCall, setDraggedReturnCall] = useState(null);
  const [draggedCspireWorkOrder, setDraggedCspireWorkOrder] = useState(null);
  const [draggingOverColumn, setDraggingOverColumn] = useState(null);
  const [draggingOverUnassigned, setDraggingOverUnassigned] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [preSelectedTechnicianId, setPreSelectedTechnicianId] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [unassignedViewMode, setUnassignedViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [unassignedTab, setUnassignedTab] = useState('month'); // 'month' | 'cspire' | 'franklin' | 'repair_queue' | 'all'
  const [finalizedSortBy, setFinalizedSortBy] = useState('name'); // 'name' or 'completedDate'
  const [isDraggingFromAllTab, setIsDraggingFromAllTab] = useState(false);
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isServiceCallModalOpen, setIsServiceCallModalOpen] = useState(false);
  const [serviceCallToEdit, setServiceCallToEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReturnServiceCall, setSelectedReturnServiceCall] = useState(null);
  const [isReturnToTechModalOpen, setIsReturnToTechModalOpen] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [dockHeight, setDockHeight] = useState(256); // Height in pixels (default 256px = h-64)
  const [isDockExpanded, setIsDockExpanded] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [selectedClientForModal, setSelectedClientForModal] = useState(null);
  const [isClientDetailsModalOpen, setIsClientDetailsModalOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);
  const [isClientFormModalOpen, setIsClientFormModalOpen] = useState(false);
  const [isOilFiltersModalOpen, setIsOilFiltersModalOpen] = useState(false);
  const [isFuelFiltersModalOpen, setIsFuelFiltersModalOpen] = useState(false);
  const [isMonthPairComparisonOpen, setIsMonthPairComparisonOpen] = useState(false);
  const [selectedCspireWorkOrder, setSelectedCspireWorkOrder] = useState(null);
  const [isCspireAssignModalOpen, setIsCspireAssignModalOpen] = useState(false);
  const [isCspireWorkOrderModalOpen, setIsCspireWorkOrderModalOpen] = useState(false);
  const [selectedCspireSite, setSelectedCspireSite] = useState(null);
  const [isCspireSiteModalOpen, setIsCspireSiteModalOpen] = useState(false);
  const [isCspireCreateModalOpen, setIsCspireCreateModalOpen] = useState(false);
  const [isCspireDuplicatesModalOpen, setIsCspireDuplicatesModalOpen] = useState(false);
  const [selectedFranklinSite, setSelectedFranklinSite] = useState(null);
  const [isFranklinAssignModalOpen, setIsFranklinAssignModalOpen] = useState(false);
  const [franklinImportFile, setFranklinImportFile] = useState(null);
  const [franklinDetailSiteId, setFranklinDetailSiteId] = useState(null);

  const queryClient = useQueryClient();

  // Get current month
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentMonthName = monthNames[currentMonth - 1];
  const currentYear = currentDate.getFullYear();
  const isCspireTab = unassignedTab === 'cspire';
  const isFranklinTab = unassignedTab === 'franklin';
  const isRepairQueueTab = unassignedTab === 'repair_queue';

  // Load preferred technicians from localStorage
  const [preferredTechIds, setPreferredTechIds] = useState([]);
  
  useEffect(() => {
    const loadPreferences = () => {
      const stored = localStorage.getItem('preferredTechnicians');
      if (stored) {
        try {
          setPreferredTechIds(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing technician preferences:', e);
          setPreferredTechIds([]);
        }
      }
    };

    loadPreferences();
    
    // Listen for changes to preferences (in case user updates in another tab or through profile)
    window.addEventListener('storage', loadPreferences);
    return () => window.removeEventListener('storage', loadPreferences);
  }, []);

  // Handle dock resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      // Calculate new height from bottom of viewport
      const newHeight = window.innerHeight - e.clientY;
      
      // Set min height of 100px and max height of 80% of viewport
      const minHeight = 100;
      const maxHeight = window.innerHeight * 0.8;
      const constrainedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
      
      setDockHeight(constrainedHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Month transitions no longer require an admin prompt - the cron job
  // materializes the new month's `unassigned` rows on the 1st, and any
  // unresolved rows from previous months show up in the Overdue / Carryover
  // section below until explicitly resolved.

  // Fetch technicians
  const { data: techniciansData } = useQuery(
    'technicians',
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  // Fetch unassigned clients needing service. Backend now returns one entry
  // per persistent `unassigned` ServiceCall row, bucketed via `sourceMonth`
  // ('current' for this month, 'overdue' for any earlier month/year).
  const { data: contractClientsNeedingService } = useQuery(
    ['contractClientsNeedingService'],
    () => clientsAPI.getContractClientsNeedingService()
  );

  // Fetch all active clients for "All" tab
  const { data: allActiveClientsData, isLoading: isLoadingAllClients } = useQuery(
    'allActiveClients',
    async () => {
      // Fetch all clients by making multiple requests if needed
      const allClients = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await clientsAPI.getClients({ page: currentPage, limit: 100 });
        const responseData = response.data?.data || response.data;
        const clients = responseData?.clients || [];
        
        // Only include active clients
        const activeClients = clients.filter(c => c.isActive !== false);
        allClients.push(...activeClients);
        
        const pagination = responseData?.pagination;
        
        if (!pagination || currentPage >= pagination.pages || clients.length === 0) {
          hasMore = false;
        } else {
          currentPage++;
        }
      }
      
      return { clients: allClients };
    },
    { enabled: unassignedTab === 'all' }
  );

  const { data: queuedCountData } = useQuery(
    'queuedServiceCallCount',
    async () => {
      const res = await clientsAPI.getQueuedServiceCallCount();
      return res.data?.count ?? 0;
    },
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );
  const queuedCount = typeof queuedCountData === 'number' ? queuedCountData : 0;

  const { data: queuedServiceCallsData = [], isLoading: isLoadingRepairQueue } = useQuery(
    'queuedServiceCalls',
    async () => {
      const res = await clientsAPI.getQueuedServiceCalls();
      return res.data?.serviceCalls || [];
    },
    {
      enabled: isRepairQueueTab,
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Fetch current assignments
  const { data: weeklyAssignmentsData } = useQuery(
    'weeklyAssignments',
    () => clientsAPI.getWeeklyAssignments(),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      refetchOnWindowFocus: true
    }
  );

  // Fetch return service calls
  const { data: returnServiceCallsData } = useQuery(
    'returnServiceCalls',
    () => serviceCallsAPI.getServiceCalls({ status: 'return', limit: 10000 }),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      refetchOnWindowFocus: true
    }
  );

  // Fetch completed (non-finalized) service calls
  const { data: completedServiceCallsData } = useQuery(
    'completedServiceCalls',
    () => serviceCallsAPI.getServiceCalls({ status: 'completed', limit: 10000 }),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      refetchOnWindowFocus: true
    }
  );

  const { data: cspireWorkOrdersData, isLoading: isLoadingCspireWorkOrders } = useQuery(
    'cspireWorkOrders',
    () => cspireAPI.getWorkOrders(),
    {
      enabled: isCspireTab,
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  const { data: cspireSchedulesData } = useQuery(
    'cspirePriceSchedules',
    () => cspireAPI.getPriceSchedules({ includeInactive: false }),
    { enabled: isCspireTab }
  );

  const { data: cspireSitesData } = useQuery(
    'cspireSites',
    () => cspireAPI.getSites(),
    { enabled: isCspireTab }
  );

  const { data: franklinSitesData, isLoading: isLoadingFranklinSites } = useQuery(
    'franklinSites',
    () => franklinAPI.getSites(),
    {
      enabled: isFranklinTab,
      refetchInterval: 30000,
      refetchOnWindowFocus: true
    }
  );

  // Compute an alphabetical sort key for a completed call based on its type.
  // - CSpire calls sort by site id
  // - Commercial clients sort by business (company) name
  // - Regular clients sort by last name, then first name
  // - Franklin sites fall back to site id / location name
  const getCompletedSortKey = (call) => {
    if (call?.isCspire) {
      const wo = call.cspireWorkOrder;
      return (wo?.site?.siteId || wo?.locationName || wo?.site?.locationName || '').toLowerCase();
    }
    if (call?.isFranklin) {
      const fs = call.franklinSite;
      return (fs?.siteId || fs?.locationName || '').toLowerCase();
    }
    const client = call?.client;
    if (client?.isCommercial) {
      return (client.companyName || '').toLowerCase();
    }
    const lastName = client?.contactLastName || '';
    const firstName = client?.contactFirstName || '';
    return `${lastName} ${firstName}`.trim().toLowerCase();
  };

  // Filter completed calls to separate finalized from non-finalized (current month only)
  const completedCalls = useMemo(() => {
    const calls = completedServiceCallsData?.data?.serviceCalls || [];
    const filtered = calls.filter(call => {
      if (call.status !== 'completed' || call.isFinalized) {
        return false;
      }
      
      // Only include calls completed in the current month
      if (call.completedDate) {
        const completedDate = new Date(call.completedDate);
        const completedMonth = completedDate.getMonth() + 1;
        const completedYear = completedDate.getFullYear();
        return completedMonth === currentMonth && completedYear === currentYear;
      }
      
      return false;
    });

    return filtered.sort((a, b) => {
      const aKey = getCompletedSortKey(a);
      const bKey = getCompletedSortKey(b);
      return aKey.localeCompare(bKey);
    });
  }, [completedServiceCallsData, currentMonth, currentYear]);

  const finalizedCalls = useMemo(() => {
    const calls = completedServiceCallsData?.data?.serviceCalls || [];
    return calls.filter(call => {
      if (call.status !== 'completed' || !call.isFinalized) {
        return false;
      }
      
      // Only include calls completed in the current month
      if (call.completedDate) {
        const completedDate = new Date(call.completedDate);
        const completedMonth = completedDate.getMonth() + 1;
        const completedYear = completedDate.getFullYear();
        return completedMonth === currentMonth && completedYear === currentYear;
      }
      
      return false;
    });
  }, [completedServiceCallsData, currentMonth, currentYear]);

  const returnCalls = useMemo(() => {
    return returnServiceCallsData?.data?.serviceCalls || [];
  }, [returnServiceCallsData]);

  const cspireWorkOrders = useMemo(() => {
    return cspireWorkOrdersData?.data?.workOrders || [];
  }, [cspireWorkOrdersData]);

  const selectedCspireSiteWorkOrders = useMemo(() => {
    if (!selectedCspireSite) return [];
    return cspireWorkOrders.filter(order => (
      order.site?.id === selectedCspireSite.id
      || order.site?.siteId === selectedCspireSite.siteId
    ));
  }, [cspireWorkOrders, selectedCspireSite]);

  const cspireUnassigned = useMemo(() => {
    return cspireWorkOrders.filter(order => !order.serviceCallId);
  }, [cspireWorkOrders]);

  const cspireAssigned = useMemo(() => {
    return cspireWorkOrders.filter(order => (
      order.serviceCall &&
      ['assigned', 'in_progress'].includes(order.serviceCall.status)
    ));
  }, [cspireWorkOrders]);

  const cspireReturnCalls = useMemo(() => {
    return cspireWorkOrders.filter(order => order.serviceCall?.status === 'return');
  }, [cspireWorkOrders]);

  const cspireCompletedCalls = useMemo(() => {
    const filtered = cspireWorkOrders.filter(order => (
      order.serviceCall?.status === 'completed' && !order.serviceCall?.isFinalized
    ));
    return filtered.sort((a, b) => {
      const aKey = (a.site?.siteId || a.locationName || a.site?.locationName || '').toLowerCase();
      const bKey = (b.site?.siteId || b.locationName || b.site?.locationName || '').toLowerCase();
      return aKey.localeCompare(bKey);
    });
  }, [cspireWorkOrders]);

  const cspireFinalizedCalls = useMemo(() => {
    return cspireWorkOrders.filter(order => (
      order.serviceCall?.status === 'completed' && order.serviceCall?.isFinalized
    ));
  }, [cspireWorkOrders]);

  const cspireSchedules = cspireSchedulesData?.data?.schedules || [];
  const cspireSites = useMemo(() => cspireSitesData?.data?.sites || [], [cspireSitesData]);

  const franklinSites = useMemo(() => franklinSitesData?.data?.sites || [], [franklinSitesData]);
  const franklinUnassigned = useMemo(() => franklinSites.filter(s => !s.assignedServiceCall), [franklinSites]);

  const filteredFranklinSites = useMemo(() => {
    if (!searchQuery.trim()) return franklinSites;
    const q = searchQuery.toLowerCase();
    return franklinSites.filter(s =>
      (s.siteId || '').toLowerCase().includes(q) ||
      (s.locationName || '').toLowerCase().includes(q) ||
      (s.address || '').toLowerCase().includes(q) ||
      (s.city || '').toLowerCase().includes(q) ||
      (s.company || '').toLowerCase().includes(q) ||
      (s.county || '').toLowerCase().includes(q) ||
      (s.state || '').toLowerCase().includes(q) ||
      (s.zipCode || '').toLowerCase().includes(q) ||
      (s.district || '').toLowerCase().includes(q)
    );
  }, [franklinSites, searchQuery]);

  const getCompletedDisplayName = useCallback((client, serviceCall) => {
    if (serviceCall?.isCspire) {
      const wo = serviceCall.cspireWorkOrder;
      const location = wo?.locationName || wo?.site?.locationName || wo?.site?.siteId || '';
      const woNumber = wo?.workOrderNumber || '';
      return `CSpire${location ? ` - ${location}` : ''}${woNumber ? ` (WO# ${woNumber})` : ''}`;
    }

    if (serviceCall?.isFranklin) {
      const fs = serviceCall.franklinSite;
      const siteName = fs?.locationName || fs?.siteId || 'Site';
      const company = fs?.company ? ` (${fs.company})` : '';
      return `Franklin - ${siteName}${company}`;
    }

    if (!client) return '';
    if (client.isCommercial && client.companyName) {
      return client.companyName;
    }

    const lastName = client.contactLastName || '';
    const firstName = client.contactFirstName || '';
    if (lastName || firstName) {
      return `${lastName}${lastName && firstName ? ', ' : ''}${firstName}`.trim();
    }

    return client.displayName || '';
  }, []);

  const getTechInfoBadge = useCallback((serviceCall) => {
    if (!serviceCall) return null;
    if (serviceCall.needsTechInfo) {
      return {
        label: 'Awaiting tech info',
        className: 'bg-yellow-100 text-yellow-800 border border-yellow-200'
      };
    }
    if (serviceCall.techInfoUpdatedAt && serviceCall.techInfoRequestReason) {
      return {
        label: 'Updated',
        className: 'bg-blue-100 text-blue-800 border border-blue-200'
      };
    }
    return null;
  }, []);

  const sortedFinalizedCalls = useMemo(() => {
    const calls = [...finalizedCalls];

    if (finalizedSortBy === 'completedDate') {
      return calls.sort((a, b) => {
        const aTime = a.completedDate ? new Date(a.completedDate).getTime() : 0;
        const bTime = b.completedDate ? new Date(b.completedDate).getTime() : 0;
        if (aTime !== bTime) return bTime - aTime;

        const aName = getCompletedDisplayName(a.client, a).toLowerCase();
        const bName = getCompletedDisplayName(b.client, b).toLowerCase();
        return aName.localeCompare(bName);
      });
    }

    return calls.sort((a, b) => {
      const aName = getCompletedDisplayName(a.client, a).toLowerCase();
      const bName = getCompletedDisplayName(b.client, b).toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [finalizedCalls, finalizedSortBy, getCompletedDisplayName]);

  // Assignment mutation
  const assignMutation = useMutation(
    (data) => clientsAPI.assignContractClient(data),
    {
      onSuccess: (data, variables) => {
        // Don't show generic success message here since we show specific ones based on context
        if (!variables.isPriority) {
          toast.success('Customer assigned successfully!');
        }
        queryClient.invalidateQueries(['contractClientsNeedingService']);
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('returnServiceCalls');
        queryClient.invalidateQueries('allActiveClients');
        queryClient.invalidateQueries('priorityServiceCalls'); // For repair service page
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          const errorMessages = error.response.data.errors.map(err => err.msg || err.message || 'Validation failed').join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error(error.response?.data?.message || 'Failed to assign customer');
        }
      }
    }
  );

  const cspireAssignMutation = useMutation(
    ({ workOrderId, payload }) => cspireAPI.assignWorkOrder(workOrderId, payload),
    {
      onSuccess: () => {
        toast.success('CSpire work order assigned successfully!');
        queryClient.invalidateQueries('cspireWorkOrders');
        queryClient.invalidateQueries('weeklyAssignments');
        setIsCspireAssignModalOpen(false);
        setSelectedCspireWorkOrder(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign CSpire work order');
      }
    }
  );

  const franklinAssignMutation = useMutation(
    ({ siteId, payload }) => franklinAPI.assignSite(siteId, payload),
    {
      onSuccess: () => {
        toast.success('Franklin site assigned successfully!');
        queryClient.invalidateQueries('franklinSites');
        queryClient.invalidateQueries('weeklyAssignments');
        setIsFranklinAssignModalOpen(false);
        setSelectedFranklinSite(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign Franklin site');
      }
    }
  );

  const franklinImportMutation = useMutation(
    (file) => franklinAPI.importSites(file),
    {
      onSuccess: (response) => {
        const data = response.data;
        toast.success(data.message || 'Franklin sites imported!');
        queryClient.invalidateQueries('franklinSites');
        setFranklinImportFile(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to import Franklin sites');
      }
    }
  );

  const cspireUpdateMutation = useMutation(
    ({ workOrderId, payload }) => cspireAPI.updateWorkOrder(workOrderId, payload),
    {
      onSuccess: () => {
        toast.success('CSpire work order updated!');
        queryClient.invalidateQueries('cspireWorkOrders');
        setIsCspireWorkOrderModalOpen(false);
        setSelectedCspireWorkOrder(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update CSpire work order');
      }
    }
  );

  const cspireDeleteMutation = useMutation(
    ({ workOrderId, force = false }) => cspireAPI.deleteWorkOrder(workOrderId, { force }),
    {
      onSuccess: () => {
        toast.success('CSpire work order deleted');
        queryClient.invalidateQueries('cspireWorkOrders');
        queryClient.invalidateQueries('weeklyAssignments');
        setIsCspireWorkOrderModalOpen(false);
        setSelectedCspireWorkOrder(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete CSpire work order');
      }
    }
  );

  const cspireCreateMutation = useMutation(
    (payload) => cspireAPI.createWorkOrder(payload),
    {
      onSuccess: () => {
        toast.success('CSpire work order created!');
        queryClient.invalidateQueries('cspireWorkOrders');
        setIsCspireCreateModalOpen(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create CSpire work order');
      }
    }
  );


  // Unassignment mutation
  const unassignMutation = useMutation(
    (serviceCallId) => clientsAPI.unassignContractClient(serviceCallId),
    {
      onSuccess: () => {
        toast.success('Customer unassigned successfully!');
        queryClient.invalidateQueries(['contractClientsNeedingService']);
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to unassign customer');
      }
    }
  );

  const queueMutation = useMutation(
    (payload) => clientsAPI.queueContractClient(payload),
    {
      onSuccess: () => {
        toast.success('Added to repair queue');
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
        queryClient.invalidateQueries('allActiveClients');
        queryClient.invalidateQueries(['contractClientsNeedingService']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add to repair queue');
      }
    }
  );

  const removeQueuedMutation = useMutation(
    (serviceCallId) => clientsAPI.removeQueuedServiceCall(serviceCallId),
    {
      onSuccess: () => {
        toast.success('Removed from repair queue');
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove from queue');
      }
    }
  );

  // Mark as done mutation
  const markDoneMutation = useMutation(
    ({ clientId, serviceCallId }) => clientsAPI.markClientDone(clientId, serviceCallId ? { serviceCallId } : {}),
    {
      onSuccess: () => {
        toast.success('Customer marked as done!');
        queryClient.invalidateQueries(['contractClientsNeedingService']);
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('completedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to mark customer as done');
      }
    }
  );

  // Bump to next month mutation
  const pushBackMutation = useMutation(
    ({ clientId, serviceCallId }) => clientsAPI.pushBackClient(clientId, serviceCallId ? { serviceCallId } : {}),
    {
      onSuccess: () => {
        toast.success('Customer bumped to next month!');
        queryClient.invalidateQueries(['contractClientsNeedingService']);
        queryClient.invalidateQueries('weeklyAssignments');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to bump customer');
      }
    }
  );

  // Skip to next cycle mutation
  const skipToNextCycleMutation = useMutation(
    ({ clientId, serviceCallId }) => clientsAPI.skipClientToNextCycle(clientId, serviceCallId ? { serviceCallId } : {}),
    {
      onSuccess: () => {
        toast.success('Customer moved to next assignment window!');
        queryClient.invalidateQueries(['contractClientsNeedingService']);
        queryClient.invalidateQueries('weeklyAssignments');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to skip customer');
      }
    }
  );

  // Group assignments by technician
  const assignmentsByTechnician = useMemo(() => {
    const assignments = weeklyAssignmentsData?.data?.assignments || [];
    const grouped = {};
    
    assignments.forEach(assignment => {
      const techId = assignment.technicianId;
      if (!grouped[techId]) {
        grouped[techId] = [];
      }
      grouped[techId].push(assignment);
    });

    return grouped;
  }, [weeklyAssignmentsData]);

  // Get unassigned clients based on active tab. The backend now only returns
  // rows whose ServiceCall.status is literally `unassigned`, so we no longer
  // need to subtract assigned / return / completed clients on the client.
  const unassignedClients = useMemo(() => {
    if (unassignedTab === 'cspire' || unassignedTab === 'franklin' || unassignedTab === 'repair_queue') {
      return [];
    }
    if (unassignedTab === 'all') {
      const allClients = allActiveClientsData?.clients || allActiveClientsData?.data?.clients || [];
      return allClients;
    }
    const allClients = contractClientsNeedingService?.data?.clients || [];
    return allClients.filter(client => (client.sourceMonth || 'current') === 'current');
  }, [unassignedTab, contractClientsNeedingService, allActiveClientsData]);

  // Overdue / carryover rows: persistent `unassigned` rows from earlier
  // months that haven't been explicitly resolved.
  const overdueClients = useMemo(() => {
    const allClients = contractClientsNeedingService?.data?.clients || [];
    return allClients.filter(client => client.sourceMonth === 'overdue');
  }, [contractClientsNeedingService]);

  // Filter unassigned clients by search query
  const filteredUnassignedClients = useMemo(() => {
    if (!searchQuery.trim()) return unassignedClients;
    
    const query = searchQuery.toLowerCase();
    return unassignedClients.filter(client => {
      const companyName = (client.companyName || '').toLowerCase();
      const contactName = `${client.contactFirstName || ''} ${client.contactLastName || ''}`.toLowerCase();
      const email = (client.email || '').toLowerCase();
      const city = (client.city || '').toLowerCase();
      const state = (client.state || '').toLowerCase();
      const address = (client.address || '').toLowerCase();
      const phone = (client.phone || '').toLowerCase();
      
      return companyName.includes(query) ||
             contactName.includes(query) ||
             email.includes(query) ||
             city.includes(query) ||
             state.includes(query) ||
             address.includes(query) ||
             phone.includes(query);
    });
  }, [unassignedClients, searchQuery]);

  const filteredCspireWorkOrders = useMemo(() => {
    if (!searchQuery.trim()) return cspireUnassigned;
    const query = searchQuery.toLowerCase();
    return cspireUnassigned.filter((order) => {
      const siteId = (order.site?.siteId || '').toLowerCase();
      const location = (order.locationName || order.site?.locationName || '').toLowerCase();
      const workOrderNumber = (order.workOrderNumber || '').toLowerCase();
      const description = (order.description || '').toLowerCase();
      const address = (order.site?.addressLine1 || '').toLowerCase();
      return siteId.includes(query)
        || location.includes(query)
        || workOrderNumber.includes(query)
        || description.includes(query)
        || address.includes(query);
    });
  }, [cspireUnassigned, searchQuery]);

  const filteredQueuedServiceCalls = useMemo(() => {
    const list = queuedServiceCallsData || [];
    if (!searchQuery.trim()) return list;
    const query = searchQuery.toLowerCase();
    return list.filter((sc) => {
      const client = sc.client;
      if (!client) return false;
      const companyName = (client.companyName || '').toLowerCase();
      const contactName = `${client.contactFirstName || ''} ${client.contactLastName || ''}`.toLowerCase();
      const email = (client.email || '').toLowerCase();
      const city = (client.city || '').toLowerCase();
      const state = (client.state || '').toLowerCase();
      const address = (client.address || '').toLowerCase();
      const phone = (client.phone || '').toLowerCase();
      const notes = (sc.customerNotes || '').toLowerCase();
      return companyName.includes(query)
        || contactName.includes(query)
        || email.includes(query)
        || city.includes(query)
        || state.includes(query)
        || address.includes(query)
        || phone.includes(query)
        || notes.includes(query);
    });
  }, [queuedServiceCallsData, searchQuery]);

  // Group unassigned clients by location (for grouped view)
  const unassignedByLocation = useMemo(() => {
    const grouped = {};
    
    filteredUnassignedClients.forEach(client => {
      const city = client.city || 'Unknown City';
      const state = client.state || 'Unknown State';
      const locationKey = `${city}, ${state}`;
      if (!grouped[locationKey]) {
        grouped[locationKey] = {
          city: city,
          state: state,
          clients: []
        };
      }
      grouped[locationKey].clients.push(client);
    });

    return Object.values(grouped).sort((a, b) => {
      const stateA = a.state || '';
      const stateB = b.state || '';
      const cityA = a.city || '';
      const cityB = b.city || '';
      
      if (stateA !== stateB) return stateA.localeCompare(stateB);
      return cityA.localeCompare(cityB);
    });
  }, [filteredUnassignedClients]);

  const allTechnicians = techniciansData?.data?.users?.filter(tech => tech.isActive) || [];
  
  // Filter technicians based on user preferences
  // If no preferences set (empty array), show all technicians
  const technicians = useMemo(() => {
    if (preferredTechIds.length === 0) {
      return allTechnicians;
    }
    return allTechnicians.filter(tech => preferredTechIds.includes(tech.id));
  }, [allTechnicians, preferredTechIds]);

  const contractServiceClients = contractClientsNeedingService?.data?.clients || [];
  const totalClientsForMonth = contractServiceClients.filter(client => (client.sourceMonth || 'current') === 'current').length;
  const totalFinalized = finalizedCalls.length;
  const totalRemaining = totalClientsForMonth - totalFinalized;
  const totalAssigned = (weeklyAssignmentsData?.data?.assignments || []).length;

  const stats = [
    {
      title: `Total for ${currentMonthName}`,
      value: totalClientsForMonth || 0,
      icon: Calendar,
      color: 'text-gray-600',
    },
    {
      title: 'Remaining',
      value: totalRemaining > 0 ? totalRemaining : 0,
      icon: Clock,
      color: 'text-orange-600',
    },
    {
      title: 'Assigned',
      value: totalAssigned || 0,
      icon: Users,
      color: 'text-blue-600',
    },
    {
      title: 'Returns',
      value: (returnCalls?.length || 0),
      icon: RotateCcw,
      color: 'text-yellow-600',
    },
    {
      title: 'Completed',
      value: (completedCalls?.length || 0),
      icon: CheckCircle,
      color: 'text-green-600',
    },
    {
      title: 'Finalized',
      value: totalFinalized || 0,
      icon: Check,
      color: 'text-emerald-600',
    },
  ];

  const handleDragStart = (client) => {
    setDraggedClient(client);
    setDraggedAssignment(null);
    setDraggedReturnCall(null);
    setDraggedCspireWorkOrder(null);
    setIsDraggingFromAllTab(unassignedTab === 'all');
  };

  const handleAssignmentDragStart = (assignment) => {
    setDraggedAssignment(assignment);
    setDraggedClient(null);
    setDraggedReturnCall(null);
    setDraggedCspireWorkOrder(null);
    setIsDraggingFromAllTab(false);
  };

  const handleReturnDragStart = (serviceCall) => {
    setDraggedReturnCall(serviceCall);
    setDraggedClient(null);
    setDraggedAssignment(null);
    setDraggedCspireWorkOrder(null);
  };

  const handleCspireWorkOrderDragStart = (workOrder) => {
    setDraggedCspireWorkOrder(workOrder);
    setDraggedClient(null);
    setDraggedAssignment(null);
    setDraggedReturnCall(null);
    setIsDraggingFromAllTab(false);
  };

  const handleDragEnd = () => {
    setDraggedClient(null);
    setDraggedAssignment(null);
    setDraggedReturnCall(null);
    setDraggedCspireWorkOrder(null);
    setDraggingOverColumn(null);
    setDraggingOverUnassigned(false);
    setIsDraggingFromAllTab(false);
  };

  const handleDragOver = (e, technicianId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggingOverColumn(technicianId);
    setDraggingOverUnassigned(false);
  };

  const handleUnassignedDragOver = (e) => {
    if (!draggedAssignment) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggingOverUnassigned(true);
    setDraggingOverColumn(null);
  };

  const handleDrop = async (e, technicianId) => {
    e.preventDefault();
    setDraggingOverColumn(null);
    setDraggingOverUnassigned(false);

    if (!technicianId || typeof technicianId !== 'string') {
      toast.error('Invalid technician selected');
      return;
    }

    const technician = allTechnicians.find(t => t.id === technicianId);

    // Handle return call reassignment
    if (draggedReturnCall) {
      const returnCall = draggedReturnCall;
      setDraggedReturnCall(null);

      const client = returnCall.client;
      if (!client || !client.id) {
        toast.error('Customer information is missing');
        return;
      }

      // Directly reassign the return call without modal
      try {
        await serviceCallsAPI.updateServiceCall(returnCall.id, {
          status: 'assigned',
          technicianId: String(technicianId),
          customerNotes: returnCall.customerNotes || null,
          isPriority: false // Reset isPriority to false for regular service
        });
        toast.success('Return customer reassigned!');
        queryClient.invalidateQueries('returnServiceCalls');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries(['contractClientsNeedingService']);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reassign return call');
      }
      return;
    }

    // Handle reassignment (dragging from one tech to another)
    if (draggedAssignment) {
      const assignmentToReassign = draggedAssignment;
      setDraggedAssignment(null);

      if (assignmentToReassign.isCspire && assignmentToReassign.cspireWorkOrder) {
        try {
          await serviceCallsAPI.updateServiceCall(assignmentToReassign.id, {
            technicianId: String(technicianId)
          });
          toast.success('CSpire work order reassigned!');
          queryClient.invalidateQueries('weeklyAssignments');
          queryClient.invalidateQueries('cspireWorkOrders');
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to reassign CSpire work order');
        }
        return;
      }

      if (assignmentToReassign.isFranklin && assignmentToReassign.franklinSite) {
        try {
          await serviceCallsAPI.updateServiceCall(assignmentToReassign.id, {
            technicianId: String(technicianId)
          });
          toast.success('Franklin site reassigned!');
          queryClient.invalidateQueries('weeklyAssignments');
          queryClient.invalidateQueries('franklinSites');
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to reassign Franklin site');
        }
        return;
      }

      const client = assignmentToReassign.client;
      if (!client || !client.id || typeof client.id !== 'string') {
        toast.error('Customer information is missing or invalid');
        return;
      }

      // Directly reassign without modal, keeping existing notes
      try {
        const requestData = {
          clientId: String(client.id),
          technicianId: String(technicianId),
          notes: assignmentToReassign.customerNotes || '',
          ...(assignmentToReassign.id ? { serviceCallId: assignmentToReassign.id } : {})
        };
        await assignMutation.mutateAsync(requestData);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reassign customer');
      }
      return;
    }

    // Handle CSpire work order assignment (dragging from unassigned list)
    if (draggedCspireWorkOrder) {
      const workOrderToAssign = draggedCspireWorkOrder;
      setDraggedCspireWorkOrder(null);

      try {
        await cspireAssignMutation.mutateAsync({
          workOrderId: workOrderToAssign.id,
          payload: { technicianId: String(technicianId) }
        });
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to assign CSpire work order');
      }
      return;
    }

    // Handle new assignment (dragging from unassigned)
    if (!draggedClient) {
      return;
    }

    const clientToAssign = draggedClient;
    setDraggedClient(null);

    if (!clientToAssign.id || typeof clientToAssign.id !== 'string') {
      toast.error('Customer ID is missing or invalid');
      return;
    }

    // Directly assign without modal
    // If dragging from "All" tab, mark as priority (repair)
    try {
      const requestData = {
        clientId: String(clientToAssign.id),
        technicianId: String(technicianId),
        notes: '',
        isPriority: isDraggingFromAllTab, // Mark as repair if from "All" tab
        ...(clientToAssign.serviceCallId ? { serviceCallId: clientToAssign.serviceCallId } : {})
      };
      await assignMutation.mutateAsync(requestData);
      
      // Show success message with appropriate context
      if (isDraggingFromAllTab) {
        toast.success('Repair service assigned successfully!');
      }
      
      setIsDraggingFromAllTab(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign customer');
      setIsDraggingFromAllTab(false);
    }
  };

  const handleUpdateNotes = async (notes) => {
    if (!pendingAssignment || pendingAssignment.type !== 'edit') return;

    try {
      await serviceCallsAPI.updateServiceCall(pendingAssignment.assignmentId, {
        customerNotes: notes || null
      });
      toast.success('Notes updated successfully!');
      queryClient.invalidateQueries('weeklyAssignments');
      setIsNotesModalOpen(false);
      setPendingAssignment(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update notes');
    }
  };

  const handleConfirmAssignment = async (notes) => {
    if (!pendingAssignment) return;

    const { type, client, technicianId, serviceCall } = pendingAssignment;

    try {
      if (type === 'return') {
        // Update the return call to assigned status and assign to technician
        await serviceCallsAPI.updateServiceCall(serviceCall.id, {
          status: 'assigned',
          technicianId: String(technicianId),
          customerNotes: notes || null,
          isPriority: false // Reset isPriority to false for regular service
        });
        toast.success('Return customer reassigned!');
        queryClient.invalidateQueries('returnServiceCalls');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries(['contractClientsNeedingService']);
      } else if (type === 'queue_edit') {
        await clientsAPI.updateQueuedServiceCall(pendingAssignment.serviceCallId, {
          customerNotes: notes || null
        });
        toast.success('Notes updated successfully!');
        queryClient.invalidateQueries('queuedServiceCalls');
        queryClient.invalidateQueries('queuedServiceCallCount');
        setIsNotesModalOpen(false);
        setPendingAssignment(null);
        return;
      } else if (type === 'edit') {
        // Handle edit separately
        await handleUpdateNotes(notes);
        return;
      } else {
        // New assignment or reassignment
        const requestData = {
          clientId: String(client.id),
          technicianId: String(technicianId),
          notes: notes || '',
          ...(client.serviceCallId ? { serviceCallId: client.serviceCallId } : {})
        };

        await assignMutation.mutateAsync(requestData);
      }

      setIsNotesModalOpen(false);
      setPendingAssignment(null);
    } catch (error) {
      if (type === 'queue_edit') {
        toast.error(error.response?.data?.message || 'Failed to update notes');
        return;
      }
      // Error handling is done in the mutation
      if (type !== 'return' && type !== 'edit') {
        // For return and edit, we already show error in their respective handlers
        toast.error(error.response?.data?.message || 'Failed to assign customer');
      }
    }
  };

  const handleUnassignedDrop = async (e) => {
    e.preventDefault();
    setDraggingOverUnassigned(false);
    setDraggingOverColumn(null);

    if (!draggedAssignment) return;

    const assignmentToUnassign = draggedAssignment;
    setDraggedAssignment(null);

    try {
      if (assignmentToUnassign.isCspire && assignmentToUnassign.cspireWorkOrder) {
        await serviceCallsAPI.deleteServiceCall(assignmentToUnassign.id);
        toast.success('CSpire work order unassigned!');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('cspireWorkOrders');
      } else {
        await unassignMutation.mutateAsync(assignmentToUnassign.id);
      }
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleAssignClick = async (client) => {
    setSelectedClient(client);
    setPreSelectedTechnicianId(null);
    setIsAssignModalOpen(true);
  };

  const handleQueueClick = (client) => {
    queueMutation.mutate({
      clientId: client.id,
      notes: '',
      ...(client.serviceCallId ? { serviceCallId: client.serviceCallId } : {})
    });
  };

  const handleEditRepairQueueNotes = (serviceCall) => {
    if (!serviceCall?.client) return;
    setPendingAssignment({
      type: 'queue_edit',
      client: serviceCall.client,
      serviceCallId: serviceCall.id,
      existingNotes: serviceCall.customerNotes || ''
    });
    setIsNotesModalOpen(true);
  };

  const handleRemoveFromRepairQueue = (serviceCallId) => {
    if (!window.confirm('Remove this customer from the repair queue?')) return;
    removeQueuedMutation.mutate(serviceCallId);
  };

  const handleMarkDone = async (client) => {
    setActionMenuOpen(null);
    if (window.confirm('Mark this customer as done for this service cycle?')) {
      markDoneMutation.mutate({
        clientId: client.id,
        serviceCallId: client.serviceCallId
      });
    }
  };

  const handlePushBack = async (client) => {
    setActionMenuOpen(null);
    if (window.confirm('Bump this customer to next month? They will appear next month but still appear in their next cycle month.')) {
      pushBackMutation.mutate({
        clientId: client.id,
        serviceCallId: client.serviceCallId
      });
    }
  };

  const handleSkipToNextCycle = async (client) => {
    setActionMenuOpen(null);
    if (window.confirm('Move this customer to the next assignment window? This will skip them for the current cycle.')) {
      skipToNextCycleMutation.mutate({
        clientId: client.id,
        serviceCallId: client.serviceCallId
      });
    }
  };

  const handleServiceCallClick = async (serviceCall) => {
    // Fetch full service call details
    try {
      const response = await serviceCallsAPI.getServiceCall(serviceCall.id);
      setSelectedServiceCall(response.data.serviceCall);
      setIsServiceCallModalOpen(true);
    } catch (error) {
      toast.error('Failed to load service call details');
    }
  };

  const handleReturnToTech = (serviceCall) => {
    setSelectedReturnServiceCall(serviceCall);
    setIsReturnToTechModalOpen(true);
  };

  const handleConfirmReturnToTech = async (reason) => {
    if (!selectedReturnServiceCall || !reason) return;

    try {
      await serviceCallsAPI.updateServiceCall(selectedReturnServiceCall.id, {
        needsTechInfo: true,
        techInfoRequestReason: reason,
        techInfoRequestedAt: new Date().toISOString(),
        techInfoUpdatedAt: null
      });
      toast.success('Request sent to technician!');
      queryClient.invalidateQueries('completedServiceCalls');
      queryClient.invalidateQueries(['serviceCall', selectedReturnServiceCall.id]);
      setIsReturnToTechModalOpen(false);
      setSelectedReturnServiceCall(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send request');
    }
  };

  const handleEditNotes = (assignment) => {
    const technician = allTechnicians.find(t => t.id === assignment.technicianId);
    setPendingAssignment({
      type: 'edit',
      client: assignment.client,
      technician,
      technicianId: assignment.technicianId,
      assignmentId: assignment.id,
      existingNotes: assignment.customerNotes || ''
    });
    setIsNotesModalOpen(true);
  };

  const handleTechCardClick = (assignment) => {
    if (!assignment.client) return;
    setSelectedClientForModal(assignment.client);
    setIsClientDetailsModalOpen(true);
  };

  const handleEditClient = (client) => {
    setClientToEdit(client);
    setIsClientFormModalOpen(true);
  };

  const handleCspireAssign = async (formData) => {
    if (!selectedCspireWorkOrder) return;
    try {
      if (formData.scheduleId && formData.scheduleId !== selectedCspireWorkOrder.scheduleId) {
        await cspireUpdateMutation.mutateAsync({
          workOrderId: selectedCspireWorkOrder.id,
          payload: { scheduleId: formData.scheduleId }
        });
      }

      const assignPayload = {
        technicianId: formData.technicianId
      };
      if (formData.scheduledDate) {
        assignPayload.scheduledDate = formData.scheduledDate;
      }
      if (formData.notes) {
        assignPayload.description = formData.notes;
      }

      await cspireAssignMutation.mutateAsync({
        workOrderId: selectedCspireWorkOrder.id,
        payload: assignPayload
      });
    } catch (error) {
      // Errors handled in mutations
    }
  };

  const handleCspireWorkOrderSave = async (formData) => {
    if (!selectedCspireWorkOrder) return;
    try {
      await cspireUpdateMutation.mutateAsync({
        workOrderId: selectedCspireWorkOrder.id,
        payload: {
          scheduleId: formData.scheduleId || null,
          invoiceInstructions: formData.invoiceInstructions || null
        }
      });
    } catch (error) {
      // Errors handled in mutation
    }
  };

  const handleCspireWorkOrderDelete = async (workOrder) => {
    if (!workOrder) return;
    const isAssigned = !!workOrder.serviceCallId;
    const message = isAssigned
      ? `Force delete CSpire work order ${workOrder.workOrderNumber}? This will also delete the linked service call. This cannot be undone.`
      : `Delete CSpire work order ${workOrder.workOrderNumber}? This cannot be undone.`;
    const confirmDelete = window.confirm(message);
    if (!confirmDelete) return;
    try {
      await cspireDeleteMutation.mutateAsync({ workOrderId: workOrder.id, force: isAssigned });
    } catch (error) {
      // Errors handled in mutation
    }
  };

  const handleCspireCreateWorkOrder = async (payload) => {
    await cspireCreateMutation.mutateAsync(payload);
  };

  const dockHeightStyle = isDockExpanded ? { height: `${dockHeight}px` } : { height: 'auto' };

  const sortUnassignedClientsByName = (a, b) => {
    const aName = (a.isCommercial && a.companyName)
      ? a.companyName.toLowerCase()
      : a.contactLastName.toLowerCase();
    const bName = (b.isCommercial && b.companyName)
      ? b.companyName.toLowerCase()
      : b.contactLastName.toLowerCase();
    return aName.localeCompare(bName);
  };

  const sortQueuedServiceCallsByName = (a, b) => {
    const ca = a.client;
    const cb = b.client;
    if (!ca && !cb) return 0;
    if (!ca) return 1;
    if (!cb) return -1;
    return sortUnassignedClientsByName(ca, cb);
  };

  const renderRepairQueueCard = (serviceCall) => {
    const client = serviceCall.client;
    if (!client) return null;
    const menuKey = `rq-${serviceCall.id}`;
    const isMenuOpen = actionMenuOpen === menuKey;
    const referenceMonth = currentMonth;
    const isOilChangeMonth = client.oilChangeMonth === referenceMonth;
    const displayName = client.isCommercial && client.companyName
      ? client.companyName
      : `${client.contactLastName}, ${client.contactFirstName}`;
    const serviceCycleMonths = Array.isArray(client.serviceCycleMonths) ? client.serviceCycleMonths : [];
    const sortedServiceMonths = [...serviceCycleMonths].sort((a, b) => a - b);
    const isLateClient = serviceCycleMonths.length > 0 && !serviceCycleMonths.includes(referenceMonth);

    return (
      <div
        key={serviceCall.id}
        onClick={() => {
          setSelectedClientForModal(client);
          setIsClientDetailsModalOpen(true);
        }}
        className={`relative border-2 rounded-lg px-4 py-3 cursor-pointer transition-all ${
          isLateClient
            ? 'bg-amber-50 hover:bg-amber-100 border-amber-200 hover:border-amber-400'
            : 'bg-red-50 hover:bg-red-100 border-red-300 hover:border-red-500'
        }`}
      >
        {isOilChangeMonth && (
          <div className="absolute -top-2 -right-2 z-10">
            <div className="bg-amber-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold flex items-center space-x-1 shadow-md">
              <Droplet className="h-3 w-3" />
              <span>Oil Change</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0 mr-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {displayName}
                </p>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 flex-shrink-0">
                  Priority
                </span>
                {client.isCommercial && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                    <Building2 className="h-3 w-3 mr-1" />
                    Commercial
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {sortedServiceMonths.length > 0 && sortedServiceMonths.map((month) => {
                  const isOilChange = client.oilChangeMonth === month;
                  return (
                    <span
                      key={month}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${
                        isOilChange
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : 'bg-gray-100 text-gray-700 border-transparent'
                      }`}
                    >
                      {isOilChange && <Droplet className="h-3 w-3 mr-1" />}
                      {monthNames[month - 1]}
                    </span>
                  );
                })}
                {client.lastServiceDate ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <Clock className="h-3 w-3 mr-1" />
                    Last: {formatDate(client.lastServiceDate)}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-50 text-gray-400 border border-gray-200 italic">
                    No service history
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-center flex-shrink-0 w-28">
            <ClientFlagIcons flags={client.flags} />
          </div>

          <div className="hidden md:flex items-center space-x-4 mr-4 flex-shrink-0">
            <div className="flex items-center space-x-1 text-xs text-gray-600">
              <MapPin className="h-3 w-3 text-gray-400" />
              <span>{client.city}, {client.state}</span>
            </div>
            {client.phone && (
              <div className="text-xs text-gray-600">
                {client.phone}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAssignClick(client);
              }}
              className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
            >
              Assign
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionMenuOpen(isMenuOpen ? null : menuKey);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {isMenuOpen && (
                <div className="absolute right-0 mt-1 w-52 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionMenuOpen(null);
                      handleEditRepairQueueNotes(serviceCall);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Notes</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionMenuOpen(null);
                      handleRemoveFromRepairQueue(serviceCall.id);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Remove from Queue</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="md:hidden mt-2 flex items-center space-x-3 text-xs text-gray-600 ml-1">
          <div className="flex items-center space-x-1">
            <MapPin className="h-3 w-3 text-gray-400" />
            <span>{client.city}, {client.state}</span>
          </div>
          {client.phone && (
            <span>{client.phone}</span>
          )}
        </div>
      </div>
    );
  };

  const renderUnassignedTableCard = (client) => {
    const isMenuOpen = actionMenuOpen === client.id;
    // For overdue rows the row carries its own serviceMonth; otherwise use
    // the current month for oil-change/late detection.
    const referenceMonth = client.sourceMonth === 'overdue' && client.serviceMonth
      ? client.serviceMonth
      : currentMonth;
    const isOilChangeMonth = client.oilChangeMonth === referenceMonth;
    const isOverdue = client.sourceMonth === 'overdue';
    const displayName = client.isCommercial && client.companyName 
      ? client.companyName 
      : `${client.contactLastName}, ${client.contactFirstName}`;
    const serviceCycleMonths = Array.isArray(client.serviceCycleMonths) ? client.serviceCycleMonths : [];
    const sortedServiceMonths = [...serviceCycleMonths].sort((a, b) => a - b);
    const isLateClient = serviceCycleMonths.length > 0 && !serviceCycleMonths.includes(referenceMonth);
    
    return (
      <div
        key={client.serviceCallId || client.id}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', client.id);
          handleDragStart(client);
        }}
        onDragEnd={handleDragEnd}
        onClick={() => {
          setSelectedClientForModal(client);
          setIsClientDetailsModalOpen(true);
        }}
        className={`relative border-2 rounded-lg px-4 py-3 cursor-pointer transition-all ${
          draggedClient?.id === client.id ? 'opacity-50' : ''
        } ${
          unassignedTab === 'all' 
            ? 'bg-red-50 hover:bg-red-100 border-red-300 hover:border-red-500' 
            : isLateClient
              ? 'bg-amber-50 hover:bg-amber-100 border-amber-200 hover:border-amber-400'
              : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-blue-400'
        }`}
      >
        {isOilChangeMonth && unassignedTab !== 'all' && (
          <div className="absolute -top-2 -right-2 z-10">
            <div className="bg-amber-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold flex items-center space-x-1 shadow-md">
              <Droplet className="h-3 w-3" />
              <span>Oil Change</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          {/* Left: Drag handle and Name */}
          <div className="flex items-center space-x-3 flex-1 min-w-0 mr-4">
            <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {displayName}
                </p>
                {client.isCommercial && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
                    <Building2 className="h-3 w-3 mr-1" />
                    Commercial
                  </span>
                )}
                {isOverdue && client.serviceMonth && client.serviceYear && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200 flex-shrink-0"
                    title="Carryover from a previous month - resolve explicitly"
                  >
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Carryover {monthNames[client.serviceMonth - 1]} {client.serviceYear}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {sortedServiceMonths.length > 0 && sortedServiceMonths.map((month) => {
                  const isOilChange = client.oilChangeMonth === month;
                  return (
                    <span
                      key={month}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${
                        isOilChange
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : 'bg-gray-100 text-gray-700 border-transparent'
                      }`}
                    >
                      {isOilChange && <Droplet className="h-3 w-3 mr-1" />}
                      {monthNames[month - 1]}
                    </span>
                  );
                })}
                {client.lastServiceDate ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <Clock className="h-3 w-3 mr-1" />
                    Last: {formatDate(client.lastServiceDate)}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-50 text-gray-400 border border-gray-200 italic">
                    No service history
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Center: Flags */}
          <div className="hidden md:flex items-center justify-center flex-shrink-0 w-28">
            <ClientFlagIcons flags={client.flags} />
          </div>

          {/* Middle: Location and Contact Info */}
          <div className="hidden md:flex items-center space-x-4 mr-4 flex-shrink-0">
            <div className="flex items-center space-x-1 text-xs text-gray-600">
              <MapPin className="h-3 w-3 text-gray-400" />
              <span>{client.city}, {client.state}</span>
            </div>
            {client.phone && (
              <div className="text-xs text-gray-600">
                {client.phone}
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {unassignedTab === 'all' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQueueClick(client);
                }}
                disabled={queueMutation.isLoading}
                className="px-3 py-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors disabled:opacity-50"
              >
                Queue
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAssignClick(client);
              }}
              className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
            >
              Assign
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionMenuOpen(isMenuOpen ? null : client.id);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              {isMenuOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkDone(client);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Check className="h-4 w-4" />
                    <span>Mark as Done</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePushBack(client);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <ArrowRight className="h-4 w-4" />
                    <span>Bump to Next Month</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSkipToNextCycle(client);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <SkipForward className="h-4 w-4" />
                    <span>Skip to Next Cycle</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Mobile-only row for location and contact */}
        <div className="md:hidden mt-2 flex items-center space-x-3 text-xs text-gray-600 ml-7">
          <div className="flex items-center space-x-1">
            <MapPin className="h-3 w-3 text-gray-400" />
            <span>{client.city}, {client.state}</span>
          </div>
          {client.phone && (
            <span>{client.phone}</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Center Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Scrollable Main Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-6 space-y-6">
            {/* Header with Current Month */}
            <div className="bg-white shadow rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Regular Service</h1>
                  <p className="mt-1 text-sm text-gray-600 flex items-center gap-2">
                    <span>
                      Working on: <span className="font-semibold text-blue-600">{currentMonthName} {currentYear}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsMonthPairComparisonOpen(true)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Compare month pair vs Regular Service"
                      aria-label="Compare month pair vs Regular Service"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </button>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsOilFiltersModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Monthly Oil Filters
                  </button>
                  <button
                    onClick={() => setIsFuelFiltersModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    <Droplet className="h-4 w-4 mr-2" />
                    Monthly Fuel Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Overdue / Carryover - persistent unassigned rows from prior months */}
            {overdueClients.length > 0 && (
              <div className="bg-white shadow rounded-lg border-l-4 border-red-500 mb-4">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      <h2 className="text-lg font-semibold text-gray-900">
                        Overdue / Carryover
                      </h2>
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-600 text-white rounded-full">
                        {overdueClients.length}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Unassigned service calls from previous months. Resolve each one explicitly - they will never auto-clear.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {overdueClients.map(client => renderUnassignedTableCard(client))}
                  </div>
                </div>
              </div>
            )}

            {/* Unassigned Clients */}
            <div
              className={`bg-white shadow rounded-lg ${
                draggingOverUnassigned ? 'ring-2 ring-blue-500 ring-offset-2' : ''
              } transition-all`}
              onDrop={handleUnassignedDrop}
              onDragOver={handleUnassignedDragOver}
            >
          <div className="px-4 py-5 sm:p-6">
            {/* Tabs */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => {
                    setUnassignedTab('month');
                    setUnassignedViewMode('grouped');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    unassignedTab === 'month'
                      ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {currentMonthName}
                  {unassignedTab === 'month' && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded-full">
                      {filteredUnassignedClients.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setUnassignedTab('cspire');
                    setUnassignedViewMode('table');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    unassignedTab === 'cspire'
                      ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  CSpire
                  {unassignedTab === 'cspire' && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-indigo-600 text-white rounded-full">
                      {cspireUnassigned.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setUnassignedTab('franklin');
                    setUnassignedViewMode('table');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    unassignedTab === 'franklin'
                      ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Franklin
                  {unassignedTab === 'franklin' && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-emerald-600 text-white rounded-full">
                      {franklinSites.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUnassignedTab('repair_queue');
                    setUnassignedViewMode('table');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center ${
                    unassignedTab === 'repair_queue'
                      ? 'bg-amber-100 text-amber-700 border-b-2 border-amber-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Repair Queue
                  <span
                    className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                      unassignedTab === 'repair_queue' ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {queuedCount}
                  </span>
                </button>
                <button
                  onClick={() => {
                    setUnassignedTab('all');
                    setUnassignedViewMode('table');
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center ${
                    unassignedTab === 'all'
                      ? 'bg-red-100 text-red-700 border-b-2 border-red-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  All Customers
                  {unassignedTab === 'all' && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-red-600 text-white rounded-full">
                      {filteredUnassignedClients.length}
                    </span>
                  )}
                </button>
              </div>
              <div className="flex items-center space-x-2">
                {isCspireTab && (
                  <>
                    <button
                      onClick={() => setIsCspireCreateModalOpen(true)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md flex items-center gap-1"
                      title="Create work order manually"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Create WO
                    </button>
                    <button
                      onClick={() => setIsCspireDuplicatesModalOpen(true)}
                      className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-md flex items-center gap-1"
                      title="Find duplicate work orders"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Duplicates
                    </button>
                  </>
                )}
                {isFranklinTab && (
                  <label className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md flex items-center gap-1 cursor-pointer">
                    <Plus className="h-3.5 w-3.5" />
                    Import Sites
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          franklinImportMutation.mutate(file);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                )}
                {!isCspireTab && !isFranklinTab && !isRepairQueueTab && (
                  <>
                    <button
                      onClick={() => setUnassignedViewMode('grouped')}
                      className={`p-2 rounded-md transition-colors ${
                        unassignedViewMode === 'grouped'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      title="Grouped by location"
                    >
                      <Grid3x3 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setUnassignedViewMode('table')}
                      className={`p-2 rounded-md transition-colors ${
                        unassignedViewMode === 'table'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      title="Table view"
                    >
                      <List className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setUnassignedViewMode('map')}
                      className={`p-2 rounded-md transition-colors ${
                        unassignedViewMode === 'map'
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      title="Map view"
                    >
                      <Map className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {/* Loading state for All Clients tab */}
            {unassignedTab === 'all' && isLoadingAllClients && (
              <div className="mb-4 flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <Loader2 className="h-6 w-6 animate-spin text-red-600" />
                  <span className="text-sm text-gray-600">Loading all customers...</span>
                </div>
              </div>
            )}
            {unassignedTab === 'cspire' && isLoadingCspireWorkOrders && (
              <div className="mb-4 flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                  <span className="text-sm text-gray-600">Loading CSpire work orders...</span>
                </div>
              </div>
            )}
            {isFranklinTab && isLoadingFranklinSites && (
              <div className="mb-4 flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                  <span className="text-sm text-gray-600">Loading Franklin sites...</span>
                </div>
              </div>
            )}
            {isRepairQueueTab && isLoadingRepairQueue && (
              <div className="mb-4 flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                  <span className="text-sm text-gray-600">Loading repair queue...</span>
                </div>
              </div>
            )}

            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={isCspireTab
                    ? 'Search CSpire work orders by WO, site, or location...'
                    : isFranklinTab
                    ? 'Search Franklin sites by name, location, company, city...'
                    : isRepairQueueTab
                    ? 'Search repair queue by customer, location, phone, or notes...'
                    : 'Search customers by name, company, location, email, phone...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            {draggingOverUnassigned && !isLoadingAllClients && (
              <div className="mb-4 p-3 bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg text-center">
                <p className="text-sm text-blue-700">Drop here to unassign</p>
              </div>
            )}

            {isFranklinTab ? (
              !isLoadingFranklinSites && filteredFranklinSites.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{searchQuery ? 'No Franklin sites match your search.' : 'No Franklin sites found. Use Import Sites to add them.'}</p>
                </div>
              ) : !isLoadingFranklinSites && (
                <div className="space-y-2">
                  {filteredFranklinSites.map((site) => {
                    const serviceCutoff = new Date();
                    serviceCutoff.setMonth(serviceCutoff.getMonth() - (site.serviceCycleMonths || 6));
                    const needsService = !site.lastServiceDate || new Date(site.lastServiceDate) < serviceCutoff;
                    const isAssigned = !!site.assignedServiceCall;

                    return (
                      <div
                        key={site.id}
                        onClick={() => setFranklinDetailSiteId(site.id)}
                        className={`border rounded-lg p-4 transition-all cursor-pointer ${
                          needsService
                            ? 'border-red-300 bg-red-50 hover:shadow-md hover:border-red-400'
                            : 'border-emerald-200 bg-emerald-50 hover:shadow-md hover:border-emerald-400'
                        }`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">
                                {site.locationName || site.siteId}
                              </span>
                              {needsService && (
                                <span className="px-2 py-0.5 text-xs font-semibold bg-red-600 text-white rounded-full">
                                  Needs Service
                                </span>
                              )}
                              {isAssigned && (
                                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded-full">
                                  Assigned: {site.assignedServiceCall.technician?.firstName} {site.assignedServiceCall.technician?.lastName}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              <span className="font-medium">{site.company}</span>
                              {' • '}
                              {site.siteId}
                              {site.address && ` • ${site.address}`}
                            </div>
                            <div className="text-xs text-gray-500">
                              {[site.city, site.county, site.state, site.zipCode].filter(Boolean).join(', ')}
                              {site.backupPower && ` • ${site.backupPower}`}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Last Service: {site.lastServiceDate ? formatDate(site.lastServiceDate) : 'None'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!isAssigned && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedFranklinSite(site);
                                  setIsFranklinAssignModalOpen(true);
                                }}
                                className="px-3 py-1 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700"
                              >
                                Assign
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : isCspireTab ? (
              !isLoadingCspireWorkOrders && filteredCspireWorkOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{searchQuery ? 'No CSpire work orders match your search.' : 'No unassigned CSpire work orders.'}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredCspireWorkOrders.map((workOrder) => (
                    <div
                      key={workOrder.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', workOrder.id);
                        handleCspireWorkOrderDragStart(workOrder);
                      }}
                      onDragEnd={handleDragEnd}
                      className="border border-indigo-200 rounded-lg p-4 bg-indigo-50 cursor-pointer hover:shadow-md hover:border-indigo-400 transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div
                          className="flex-1 min-w-0"
                          onClick={() => {
                            if (workOrder.site) {
                              setSelectedCspireSite(workOrder.site);
                              setIsCspireSiteModalOpen(true);
                            }
                          }}
                        >
                          <div className="text-sm font-semibold text-indigo-900">
                            {workOrder.locationName || workOrder.site?.locationName || 'CSpire Site'}
                          </div>
                          <div className="text-sm text-gray-700">
                            WO# {workOrder.workOrderNumber}
                          </div>
                          <div className="text-xs text-gray-500">
                            Site ID: {workOrder.site?.siteId || 'N/A'} • Due: {workOrder.dueDate ? formatDate(workOrder.dueDate) : 'N/A'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCspireWorkOrder(workOrder);
                              setIsCspireWorkOrderModalOpen(true);
                            }}
                            className="px-3 py-1 text-xs font-medium text-indigo-700 bg-white border border-indigo-200 rounded hover:bg-indigo-50"
                          >
                            WO Details
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCspireWorkOrder(workOrder);
                              setIsCspireAssignModalOpen(true);
                            }}
                            className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                          >
                            Assign
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCspireWorkOrderDelete(workOrder);
                            }}
                            disabled={cspireDeleteMutation.isLoading}
                            title="Delete work order"
                            className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : isRepairQueueTab ? (
              !isLoadingRepairQueue && filteredQueuedServiceCalls.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{searchQuery ? 'No repairs match your search.' : 'No repairs in the queue.'}</p>
                </div>
              ) : !isLoadingRepairQueue ? (
                <div className="space-y-1">
                  {filteredQueuedServiceCalls
                    .slice()
                    .sort(sortQueuedServiceCallsByName)
                    .map(renderRepairQueueCard)}
                </div>
              ) : null
            ) : !isLoadingAllClients && filteredUnassignedClients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>{searchQuery ? 'No customers match your search.' : unassignedTab === 'all' ? 'No active customers found.' : 'All customers have been assigned!'}</p>
              </div>
            ) : !isLoadingAllClients && unassignedViewMode === 'grouped' ? (
              <div className="space-y-4">
                {unassignedByLocation.map((location) => (
                  <div key={`${location.city}-${location.state}`} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <MapPin className="h-5 w-5 text-blue-600" />
                      <h3 className="font-medium text-gray-900">
                        {location.city}, {location.state}
                      </h3>
                      <span className="text-sm text-gray-500">
                        ({location.clients.length} {location.clients.length === 1 ? 'customer' : 'customers'})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {location.clients
                        .slice()
                        .sort(sortUnassignedClientsByName)
                        .map(renderUnassignedTableCard)}
                    </div>
                  </div>
                ))}
              </div>
            ) : !isLoadingAllClients && unassignedViewMode === 'table' ? (
              <div className="space-y-1">
                {filteredUnassignedClients
                  .slice()
                  .sort(sortUnassignedClientsByName)
                  .map(renderUnassignedTableCard)}
              </div>
            ) : !isLoadingAllClients ? (
              <ClientsMap
                clients={filteredUnassignedClients}
                onClientClick={(client) => {
                  setSelectedClientForModal(client);
                  setIsClientDetailsModalOpen(true);
                }}
                draggedClient={draggedClient}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              />
            ) : null}
              </div>
            </div>

            {isCspireTab && (
              <div className="space-y-6">
                <div className="bg-white shadow rounded-lg p-4">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    Assigned CSpire Work Orders
                  </h2>
                  {cspireAssigned.length === 0 ? (
                    <p className="text-sm text-gray-500">No assigned CSpire work orders.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Technician</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {cspireAssigned.map((order) => (
                            <tr
                              key={order.id}
                              className="hover:bg-indigo-50 cursor-pointer transition-colors"
                              onClick={() => {
                                if (order.site) {
                                  setSelectedCspireSite(order.site);
                                  setIsCspireSiteModalOpen(true);
                                }
                              }}
                            >
                              <td className="px-6 py-4">
                                <div className="text-sm font-medium text-gray-900">{order.locationName || order.site?.locationName || 'CSpire Site'}</div>
                                <div className="text-xs text-gray-500">
                                  WO# {order.workOrderNumber}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                {order.serviceCall?.technician
                                  ? `${order.serviceCall.technician.firstName} ${order.serviceCall.technician.lastName}`
                                  : 'Unassigned'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                {order.serviceCall?.status || 'N/A'}
                              </td>
                              <td className="px-6 py-4 text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => {
                                    setSelectedCspireWorkOrder(order);
                                    setIsCspireWorkOrderModalOpen(true);
                                  }}
                                  className="text-indigo-600 hover:text-indigo-900 mr-4"
                                >
                                  View Work Order
                                </button>
                                {order.serviceCall && (
                                  <button
                                    onClick={() => handleServiceCallClick(order.serviceCall)}
                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                  >
                                    View Service Call
                                  </button>
                                )}
                                <button
                                  onClick={() => handleCspireWorkOrderDelete(order)}
                                  disabled={cspireDeleteMutation.isLoading}
                                  title="Delete work order"
                                  className="text-red-600 hover:text-red-800 disabled:opacity-50 inline-flex items-center"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="bg-white shadow rounded-lg p-4">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    Return CSpire Work Orders
                  </h2>
                  {cspireReturnCalls.length === 0 ? (
                    <p className="text-sm text-gray-500">No CSpire return work orders.</p>
                  ) : (
                    <div className="space-y-2">
                      {cspireReturnCalls.map((order) => (
                        <div
                          key={order.id}
                          className="border border-yellow-200 rounded-lg p-3 bg-yellow-50 flex items-center justify-between cursor-pointer hover:shadow-md hover:border-yellow-400 transition-all"
                          onClick={() => {
                            if (order.site) {
                              setSelectedCspireSite(order.site);
                              setIsCspireSiteModalOpen(true);
                            }
                          }}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">{order.locationName || order.site?.locationName || 'CSpire Site'}</div>
                            <div className="text-xs text-gray-500">
                              WO# {order.workOrderNumber}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleServiceCallClick(order.serviceCall); }}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                            >
                              View Service Call
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCspireWorkOrderDelete(order); }}
                              disabled={cspireDeleteMutation.isLoading}
                              title="Delete work order"
                              className="p-1.5 text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white shadow rounded-lg p-4">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    Completed CSpire Work Orders
                  </h2>
                  {cspireCompletedCalls.length === 0 ? (
                    <p className="text-sm text-gray-500">No completed CSpire work orders.</p>
                  ) : (
                    <div className="space-y-2">
                      {cspireCompletedCalls.map((order) => (
                        <div
                          key={order.id}
                          className="border border-green-200 rounded-lg p-3 bg-green-50 flex items-center justify-between cursor-pointer hover:shadow-md hover:border-green-400 transition-all"
                          onClick={() => {
                            if (order.site) {
                              setSelectedCspireSite(order.site);
                              setIsCspireSiteModalOpen(true);
                            }
                          }}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">{order.locationName || order.site?.locationName || 'CSpire Site'}</div>
                            <div className="text-xs text-gray-500">WO# {order.workOrderNumber}</div>
                            <div className="text-xs text-gray-500">
                              Completed: {order.serviceCall?.completedDate ? formatDate(order.serviceCall.completedDate) : 'N/A'}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleServiceCallClick(order.serviceCall); }}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex-shrink-0"
                            >
                              View Service Call
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCspireWorkOrderDelete(order); }}
                              disabled={cspireDeleteMutation.isLoading}
                              title="Delete work order"
                              className="p-1.5 text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white shadow rounded-lg p-4">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">
                    Finalized CSpire Work Orders
                  </h2>
                  {cspireFinalizedCalls.length === 0 ? (
                    <p className="text-sm text-gray-500">No finalized CSpire work orders.</p>
                  ) : (
                    <div className="space-y-2">
                      {cspireFinalizedCalls.map((order) => (
                        <div
                          key={order.id}
                          className="border border-emerald-200 rounded-lg p-3 bg-emerald-50 flex items-center justify-between cursor-pointer hover:shadow-md hover:border-emerald-400 transition-all"
                          onClick={() => {
                            if (order.site) {
                              setSelectedCspireSite(order.site);
                              setIsCspireSiteModalOpen(true);
                            }
                          }}
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">{order.locationName || order.site?.locationName || 'CSpire Site'}</div>
                            <div className="text-xs text-gray-500">WO# {order.workOrderNumber}</div>
                            <div className="text-xs text-gray-500">
                              Finalized: {order.serviceCall?.completedDate ? formatDate(order.serviceCall.completedDate) : 'N/A'}
                            </div>
                            {order.serviceCall?.invoiceNumber && (
                              <div className="text-xs text-indigo-600 font-medium">Invoice: {order.serviceCall.invoiceNumber}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleServiceCallClick(order.serviceCall); }}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                            >
                              View Service Call
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCspireWorkOrderDelete(order); }}
                              disabled={cspireDeleteMutation.isLoading}
                              title="Delete work order"
                              className="p-1.5 text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isCspireTab && (
              <>
                {/* Finalized Block */}
                {finalizedCalls.length > 0 && (
                  <div className="bg-white shadow rounded-lg p-4">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">
                      Finalized Service Calls - {currentMonthName} {currentYear}
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <button
                                type="button"
                                onClick={() => setFinalizedSortBy('name')}
                                className="inline-flex items-center space-x-1 text-gray-500 hover:text-gray-700"
                              >
                                <span>Customer Name</span>
                                {finalizedSortBy === 'name' && (
                                  <ChevronDown className="h-3 w-3" />
                                )}
                              </button>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Technician
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <button
                                type="button"
                                onClick={() => setFinalizedSortBy('completedDate')}
                                className="inline-flex items-center space-x-1 text-gray-500 hover:text-gray-700"
                              >
                                <span>Completed Date</span>
                                {finalizedSortBy === 'completedDate' && (
                                  <ChevronDown className="h-3 w-3" />
                                )}
                              </button>
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedFinalizedCalls.map((serviceCall) => {
                            const client = serviceCall.client;
                            const technician = serviceCall.technician;
                            return (
                              <tr key={serviceCall.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {getCompletedDisplayName(client, serviceCall)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {technician ? `${technician.firstName} ${technician.lastName}` : 'N/A'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {serviceCall.completedDate ? formatDate(serviceCall.completedDate) : 'N/A'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => handleServiceCallClick(serviceCall)}
                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                  >
                                    View Service Call
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Could open a client details modal here
                                      // For now, just navigate or show details
                                      console.log('View customer:', client);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    View Customer
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer with Technician Columns */}
        <div 
          className={`flex-shrink-0 bg-white border-t-2 border-gray-300 transition-all duration-150 relative ${isCspireTab ? 'hidden' : ''}`}
          style={dockHeightStyle}
        >
            <div className="h-full flex flex-col">
              {/* Resize Handle */}
              {isDockExpanded && (
                <div
                  onMouseDown={handleResizeStart}
                  className={`absolute top-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-blue-500 hover:opacity-50 transition-all z-10 group ${
                    isResizing ? 'bg-blue-500 opacity-50' : ''
                  }`}
                  title="Drag to resize"
                >
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-400 rounded-full mt-0.5 group-hover:bg-blue-500 transition-colors" />
                </div>
              )}

            {/* Tech Dock Header */}
            <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Tech Dock</h2>
                <button
                  onClick={() => setIsDockExpanded(!isDockExpanded)}
                  className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>{isDockExpanded ? 'Collapse' : 'Expand'}</span>
                  {isDockExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Tech Dock - Collapsed and Expanded */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full flex overflow-x-hidden">
                {technicians.map((technician) => {
                  const assignmentCount = (assignmentsByTechnician[technician.id] || []).length;
                  const assignments = assignmentsByTechnician[technician.id] || [];
                  
                  return (
                    <div
                      key={technician.id}
                      className={`flex-1 border-r border-gray-200 last:border-r-0 ${
                        draggingOverColumn === technician.id
                          ? isDraggingFromAllTab ? 'bg-red-50' : 'bg-blue-50'
                          : ''
                      } transition-colors min-w-0`}
                      onDrop={(e) => handleDrop(e, technician.id)}
                      onDragOver={(e) => handleDragOver(e, technician.id)}
                    >
                      {/* Technician Header - Always Visible */}
                      <div className={`px-3 py-2 border-b ${
                        draggingOverColumn === technician.id
                          ? isDraggingFromAllTab ? 'border-red-300 bg-red-100' : 'border-blue-300 bg-blue-100'
                          : 'border-gray-200 bg-gray-50'
                      } cursor-pointer hover:bg-gray-100`}>
                        {draggingOverColumn === technician.id && isDraggingFromAllTab && (
                          <div className="text-xs font-medium text-red-700 mb-1 flex items-center justify-center">
                            <span className="bg-red-200 px-2 py-0.5 rounded-full">Drop for Repair Service</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {technician.firstName}
                          </p>
                          <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                            {assignmentCount}
                          </span>
                        </div>
                      </div>

                      {/* Client List - Only in Expanded View */}
                      {isDockExpanded && (
                        <div className="overflow-y-auto h-[calc(100%-44px)] p-2">
                          {assignments.length === 0 ? (
                            <div className="text-center py-4 text-xs text-gray-400">
                              Drop here
                            </div>
                          ) : (
                            <div className="space-y-1">
                              {assignments.map((assignment) => {
                                const client = assignment.client;
                                const cspireWorkOrder = assignment.cspireWorkOrder;
                                const franklinSite = assignment.franklinSite;
                                const isCspire = !!cspireWorkOrder || assignment.isCspire;
                                const isFranklin = !!franklinSite || assignment.isFranklin;
                                if (!client && !cspireWorkOrder && !franklinSite) return null;

                                const isDragging = draggedAssignment?.id === assignment.id;
                                const hasNotes = assignment.customerNotes && assignment.customerNotes.trim().length > 0;
                                const isPriority = assignment.isPriority === true;
                                const currentMonth = new Date().getMonth() + 1;
                                const isOilChangeMonth = client && client.oilChangeMonth === currentMonth;
                                const displayName = isFranklin
                                  ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}`
                                  : isCspire
                                  ? formatCspireAssignmentDisplayName(cspireWorkOrder)
                                  : (client.isCommercial && client.companyName
                                    ? client.companyName
                                    : `${client.contactFirstName} ${client.contactLastName}`);
                                const location = isFranklin
                                  ? ([franklinSite?.city, franklinSite?.state].filter(Boolean).join(', ') || 'Franklin Site')
                                  : isCspire
                                  ? (cspireWorkOrder.site?.city && cspireWorkOrder.site?.state
                                    ? `${cspireWorkOrder.site.city}, ${cspireWorkOrder.site.state}`
                                    : (cspireWorkOrder.locationName || cspireWorkOrder.site?.locationName || 'CSpire Site'))
                                  : `${client.city}, ${client.state}`;
                                const isDraggable = !!client || isCspire || isFranklin;
                                
                                return (
                                  <div
                                    key={assignment.id}
                                    draggable={isDraggable}
                                    onDragStart={(e) => {
                                      if (!isDraggable) return;
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', assignment.id);
                                      handleAssignmentDragStart?.(assignment);
                                    }}
                                    onDragEnd={handleDragEnd}
                                    onClick={() => {
                                      if (isFranklin) {
                                        if (franklinSite?.id) {
                                          setFranklinDetailSiteId(franklinSite.id);
                                        }
                                        return;
                                      }
                                      if (isCspire) {
                                        if (cspireWorkOrder?.site) {
                                          setSelectedCspireSite(cspireWorkOrder.site);
                                          setIsCspireSiteModalOpen(true);
                                        }
                                        return;
                                      }
                                      if (!client) return;
                                      handleTechCardClick(assignment);
                                    }}
                                    className={`bg-white border-2 rounded p-2 cursor-pointer hover:shadow-sm transition-all ${
                                      isDragging ? 'opacity-50' : 
                                      isPriority ? 'border-red-300 hover:border-red-500' : 'border-blue-300 hover:border-blue-500'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-1">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1">
                                          <p className="text-xs font-medium text-gray-900 truncate">
                                            {displayName}
                                          </p>
                                          {isCspire && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded">
                                              CSpire
                                            </span>
                                          )}
                                          {isFranklin && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-800 rounded">
                                              Franklin
                                            </span>
                                          )}
                                          {isPriority && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                                              Repair
                                            </span>
                                          )}
                                          {isOilChangeMonth && (
                                            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded flex items-center gap-0.5">
                                              <Droplet className="h-2.5 w-2.5" />
                                              Oil
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 truncate">
                                          {location}
                                        </p>
                                        {hasNotes && (
                                          <p className="text-xs text-gray-600 italic truncate mt-0.5" title={assignment.customerNotes}>
                                            📝 {assignment.customerNotes}
                                          </p>
                                        )}
                                      </div>
                                      {client && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditNotes(assignment);
                                          }}
                                          className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-600 rounded"
                                          title="Edit notes"
                                        >
                                          <FileText className="h-3 w-3" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {technicians.length === 0 && allTechnicians.length > 0 && (
                  <div className="flex-1 text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium mb-1">No technicians in your dock</p>
                    <p className="text-sm">Go to Profile Settings to add technicians</p>
                  </div>
                )}
                {allTechnicians.length === 0 && (
                  <div className="flex-1 text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No active technicians found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Fixed */}
      {!isCspireTab ? (
        <aside className="w-80 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white">
          <div className="p-4 space-y-4">
            {/* Completed Block */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-medium text-gray-900">Completed ({currentMonthName})</h3>
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                  {completedCalls.length}
                </span>
              </div>
              <div className="space-y-2">
            {completedCalls.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No completed service calls</p>
            ) : (
              completedCalls.map((serviceCall) => (
                <ServiceCallCard
                  key={serviceCall.id}
                  serviceCall={serviceCall}
                  onClick={() => handleServiceCallClick(serviceCall)}
                  showCompletedDate={true}
                  statusBadge={getTechInfoBadge(serviceCall)}
                />
              ))
              )}
            </div>
          </div>

            {/* Return Block */}
            <div className="bg-white shadow rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <RotateCcw className="h-5 w-5 text-yellow-600" />
                <h3 className="text-lg font-medium text-gray-900">Returns</h3>
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  {returnCalls.length}
                </span>
              </div>
              <div className="space-y-2">
            {returnCalls.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No return clients</p>
            ) : (
              returnCalls.map((serviceCall) => (
                <ServiceCallCard
                  key={serviceCall.id}
                  serviceCall={serviceCall}
                  onClick={() => handleServiceCallClick(serviceCall)}
                  onReassign={() => {}}
                  isDragging={draggedReturnCall?.id === serviceCall.id}
                  onDragStart={handleReturnDragStart}
                  onDragEnd={handleDragEnd}
                />
              ))
            )}
          </div>
        </div>

            {/* Stats - Compact Version */}
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Statistics</h3>
              <div className="space-y-2">
                {stats.map((stat) => {
                  const Icon = stat.icon;
                  const displayValue = typeof stat.value === 'number' ? stat.value : (parseInt(stat.value) || 0);
                  return (
                    <div key={stat.title} className="flex items-center justify-between py-1">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <Icon className={`h-4 w-4 flex-shrink-0 ${stat.color}`} />
                        <span className="text-xs text-gray-600 truncate">{stat.title}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 ml-2">{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>
      ) : (
        <aside className="w-80 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white">
          <div className="p-4 space-y-4">
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">CSpire Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Unassigned</span>
                  <span className="font-semibold text-gray-900">{cspireUnassigned.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Assigned</span>
                  <span className="font-semibold text-gray-900">{cspireAssigned.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Returns</span>
                  <span className="font-semibold text-gray-900">{cspireReturnCalls.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Completed</span>
                  <span className="font-semibold text-gray-900">{cspireCompletedCalls.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Finalized</span>
                  <span className="font-semibold text-gray-900">{cspireFinalizedCalls.length}</span>
                </div>
              </div>
            </div>
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Price Schedules</h3>
              {cspireSchedules.length === 0 ? (
                <p className="text-sm text-gray-500">No active schedules.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {cspireSchedules.slice(0, 5).map((schedule) => (
                    <li key={schedule.id} className="flex items-center justify-between">
                      <span className="text-gray-700 truncate">{schedule.name}</span>
                      <span className="text-gray-900">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(schedule.amount || 0)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Assignment Modal */}
      {selectedClient && (
        <AssignClientModal
          client={selectedClient}
          isOpen={isAssignModalOpen}
          onClose={() => {
            setIsAssignModalOpen(false);
            setSelectedClient(null);
            setPreSelectedTechnicianId(null);
          }}
          preSelectedTechnicianId={preSelectedTechnicianId}
        />
      )}

      {/* CSpire Assign Modal */}
      {selectedCspireWorkOrder && (
        <CspireAssignModal
          workOrder={selectedCspireWorkOrder}
          isOpen={isCspireAssignModalOpen}
          technicians={allTechnicians}
          schedules={cspireSchedules}
          onAssign={handleCspireAssign}
          onClose={() => {
            setIsCspireAssignModalOpen(false);
            setSelectedCspireWorkOrder(null);
          }}
        />
      )}

      {/* CSpire Work Order Modal */}
      {selectedCspireWorkOrder && (
        <CspireWorkOrderModal
          workOrder={selectedCspireWorkOrder}
          isOpen={isCspireWorkOrderModalOpen}
          schedules={cspireSchedules}
          onSave={handleCspireWorkOrderSave}
          onDelete={handleCspireWorkOrderDelete}
          onClose={() => {
            setIsCspireWorkOrderModalOpen(false);
            setSelectedCspireWorkOrder(null);
          }}
        />
      )}

      {selectedCspireSite && (
        <CspireSiteDetailsModal
          site={selectedCspireSite}
          workOrders={selectedCspireSiteWorkOrders}
          isOpen={isCspireSiteModalOpen}
          onViewServiceCall={handleServiceCallClick}
          onClose={() => {
            setIsCspireSiteModalOpen(false);
            setSelectedCspireSite(null);
          }}
        />
      )}

      <FranklinSiteDetailsModal
        siteId={franklinDetailSiteId}
        isOpen={!!franklinDetailSiteId}
        onViewServiceCall={handleServiceCallClick}
        onClose={() => setFranklinDetailSiteId(null)}
      />

      <CspireCreateWorkOrderModal
        isOpen={isCspireCreateModalOpen}
        onClose={() => setIsCspireCreateModalOpen(false)}
        sites={cspireSites}
        onCreate={handleCspireCreateWorkOrder}
      />

      <CspireDuplicatesModal
        isOpen={isCspireDuplicatesModalOpen}
        onClose={() => setIsCspireDuplicatesModalOpen(false)}
        onDelete={handleCspireWorkOrderDelete}
      />

      {/* Franklin Assign Modal */}
      {selectedFranklinSite && isFranklinAssignModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Assign Franklin Site</h3>
              <button onClick={() => { setIsFranklinAssignModalOpen(false); setSelectedFranklinSite(null); }} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="bg-emerald-50 p-3 rounded-md mb-4 text-sm">
              <div className="font-medium text-gray-900">{selectedFranklinSite.locationName || selectedFranklinSite.siteId}</div>
              <div className="text-gray-600">{selectedFranklinSite.company} • {selectedFranklinSite.siteId}</div>
              {selectedFranklinSite.address && <div className="text-gray-500">{selectedFranklinSite.address}</div>}
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const technicianId = formData.get('technicianId');
              if (!technicianId) { toast.error('Please select a technician'); return; }
              franklinAssignMutation.mutate({
                siteId: selectedFranklinSite.id,
                payload: {
                  technicianId,
                  scheduledDate: formData.get('scheduledDate') || undefined,
                  description: formData.get('notes') || undefined
                }
              });
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Technician *</label>
                <select name="technicianId" required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500">
                  <option value="">Select a technician</option>
                  {allTechnicians.map((tech) => (
                    <option key={tech.id} value={tech.id}>{tech.firstName} {tech.lastName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date (optional)</label>
                <input type="date" name="scheduledDate" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea name="notes" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setIsFranklinAssignModalOpen(false); setSelectedFranklinSite(null); }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={franklinAssignMutation.isLoading} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50">
                  {franklinAssignMutation.isLoading ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Service Call View Modal */}
      <ServiceCallDetailsModal
        serviceCall={selectedServiceCall}
        isOpen={isServiceCallModalOpen}
        onClose={() => {
          setIsServiceCallModalOpen(false);
          setSelectedServiceCall(null);
        }}
        onReturnToTech={() => handleReturnToTech(selectedServiceCall)}
        onEdit={(serviceCall) => {
          setServiceCallToEdit(serviceCall);
          setIsEditModalOpen(true);
        }}
      />

      {/* Service Call Edit Modal */}
      <ServiceCallEditModal
        serviceCall={serviceCallToEdit}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setServiceCallToEdit(null);
        }}
      />

      <ReturnToTechModal
        serviceCall={selectedReturnServiceCall}
        isOpen={isReturnToTechModalOpen}
        onClose={() => {
          setIsReturnToTechModalOpen(false);
          setSelectedReturnServiceCall(null);
        }}
        onConfirm={handleConfirmReturnToTech}
      />

      {/* Add Notes Modal */}
      {pendingAssignment && (
        <AddNotesModal
          client={pendingAssignment.client}
          technician={pendingAssignment.technician}
          isOpen={isNotesModalOpen}
          onClose={() => {
            setIsNotesModalOpen(false);
            setPendingAssignment(null);
          }}
          onConfirm={handleConfirmAssignment}
          existingNotes={pendingAssignment.existingNotes || ''}
          isEdit={pendingAssignment.type === 'edit' || pendingAssignment.type === 'queue_edit'}
          heading={pendingAssignment.type === 'queue_edit' ? 'Edit repair queue notes' : null}
        />
      )}

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActionMenuOpen(null)}
        />
      )}

      {/* Client Details Modal */}
      <ClientDetailsModal
        client={selectedClientForModal}
        isOpen={isClientDetailsModalOpen}
        onClose={() => {
          setIsClientDetailsModalOpen(false);
          setSelectedClientForModal(null);
        }}
        onEdit={handleEditClient}
      />

      {/* Client Form Modal (for editing) */}
      <ClientFormModal
        client={clientToEdit}
        isOpen={isClientFormModalOpen}
        mode="edit"
        onClose={() => {
          setIsClientFormModalOpen(false);
          setClientToEdit(null);
        }}
      />

      {/* Monthly Oil Filters Modal */}
      <MonthlyOilFiltersModal
        isOpen={isOilFiltersModalOpen}
        onClose={() => setIsOilFiltersModalOpen(false)}
      />

      {/* Monthly Fuel Filters Modal */}
      <MonthlyFuelFiltersModal
        isOpen={isFuelFiltersModalOpen}
        onClose={() => setIsFuelFiltersModalOpen(false)}
      />

      {/* Month Pair Comparison Modal */}
      <MonthPairComparisonModal
        isOpen={isMonthPairComparisonOpen}
        onClose={() => setIsMonthPairComparisonOpen(false)}
      />
    </div>
  );
}
